import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getFirestore, collection, onSnapshot, doc, updateDoc, query, orderBy, limit, getDocs, where, setDoc, getDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { Target, Play, Plus, Grid, List, ArrowLeft, ArrowRight, LogOut, User, AlertTriangle, Shield, Menu, Wifi, WifiOff, Link, Copy, Check, ExternalLink, Loader, Smartphone, MoreVertical, X } from 'lucide-react';
import { app, auth } from '../firebase';
import { db2 } from '../firebase2';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Login from './Login';

// Nuevos componentes importados
// EspConnection ya no se importa aquí directamente porque se usa dentro de SequenceManager
import SequenceList, { SequenceForm } from './SequenceManager';
import AutoTestController from './AutoTestController';
import ManualToolControl from './ManualToolControl';
import CommunicationLog from './CommunicationLog';
import OutputVisualization from './OutputVisualization';
import CircularPositionSelector from './CircularPositionSelector';
import SequencePreview, { SequenceSnapshot } from './SequencePreview';
import AdminPanel from './AdminPanel';
import TestDescarga from './TestDescarga';
import TikTokLivePanel from './TikTokLivePanel';
import RemoteControl from './RemoteControl';
import PuntosPanel from './PuntosPanel';
import { FEATURE_TIKTOK, FEATURE_PUNTOS } from '../config/features';

// Hook personalizado de Bluetooth (similar al useBluetooth)
const useBluetooth = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [device, setDevice] = useState(null);
  const [characteristic, setCharacteristic] = useState(null);
  const [error, setError] = useState(null);
  const callbacksRef = useRef([]); // Usar ref para los callbacks para evitar problemas de clausura
  const [logs, setLogs] = useState([]); // Nuevo estado para logs

  const SERVICE_UUID = '4fafc201-1fb5-459e-8fcc-c5c9c331914b';
  const CHARACTERISTIC_UUID = 'beb5483e-36e1-4688-b7f5-ea07361b26a8';
  
  // Función auxiliar para agregar logs
  const addLog = (type, message) => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-50), { time, type, message }]);
  };

  const connect = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      addLog('info', 'Iniciando conexión Bluetooth...');

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ name: 'CNC_ROCHARAMAS_V2' }],
        optionalServices: [SERVICE_UUID]
      });

      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(SERVICE_UUID);
      const char = await service.getCharacteristic(CHARACTERISTIC_UUID);

      await char.startNotifications();
      char.addEventListener('characteristicvaluechanged', handleNotification);
      device.addEventListener('gattserverdisconnected', handleDisconnect);

      setDevice(device);
      setCharacteristic(char);
      setIsConnected(true);
      setIsConnecting(false);
      addLog('success', `Conectado a ${device.name}`);

      return true;
    } catch (err) {
      setError(err.message);
      setIsConnecting(false);
      addLog('error', `Error de conexión: ${err.message}`);
      return false;
    }
  };

  const disconnect = () => {
    if (device && device.gatt.connected) {
      device.gatt.disconnect();
      addLog('info', 'Desconectando...');
    }
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setDevice(null);
    setCharacteristic(null);
    addLog('warning', 'Dispositivo desconectado');
  };

  const handleNotification = (event) => {
    const value = new TextDecoder().decode(event.target.value);
    console.log('Raw Bluetooth value:', value);
    addLog('rx', value); // Log recibido
    
    try {
      const data = JSON.parse(value);
      console.log('Parsed Bluetooth data:', data);
      callbacksRef.current.forEach(callback => callback(data));
    } catch (e) {
      console.log('Respuesta no JSON:', value);
    }
  };

  const sendCommand = async (cmd, data = {}) => {
    if (!characteristic) {
        addLog('error', 'No se pudo enviar: No conectado');
        return false;
    }

    try {
      const command = JSON.stringify({ cmd, ...data });
      addLog('tx', command); // Log enviado
      const encoder = new TextEncoder();
      await characteristic.writeValue(encoder.encode(command));
      return true;
    } catch (err) {
      setError(err.message);
      addLog('error', `Error al enviar: ${err.message}`);
      return false;
    }
  };

  const onStatusChange = (callback) => {
    if (!callbacksRef.current.includes(callback)) {
      callbacksRef.current.push(callback);
    }
    return () => {
      callbacksRef.current = callbacksRef.current.filter(cb => cb !== callback);
    };
  };

  return {
    isConnected,
    isConnecting,
    error,
    deviceName: device?.name,
    isBluetoothAvailable: typeof navigator !== 'undefined' && !!navigator.bluetooth,
    isSecureContext: typeof window !== 'undefined' && window.isSecureContext,
    logs, // Exponemos los logs
    connect,
    disconnect,
    sendCommand,
    onStatusChange
  };
};

// Configuración por defecto de motores
const MOTOR_CONFIG_DEFAULTS = {
  velocidadHilador: 2000,
  aceleracionHilador: 10000,
  velocidadElevador: 400,
  aceleracionElevador: 800,
  pasosPorMmElevador: 80,
  velocidadPrincipalAuto: 400,
  aceleracionPrincipalAuto: 1800,
  velocidadPrincipalManual: 400,
  aceleracionPrincipalManual: 800,
  tiempoDisparo: 150,
  invertirPrincipal: false,
  invertirHilador: false,
  invertirElevador: false,
  invertirRele: false,
  anguloDentro: 0,
  anguloFuera: 145,
  umbralPasosMotor: 10,
  umbralPulsosEncoder: 5,
  ciclosSospechososMax: 2,
  inputElevadorManual: 10,
  inputPrincipalManual: 10,
  conteoFallasHilo: 0,
  delayDisparo: 500, // Tiempo de espera tras disparar (ms)
  // Motor auxiliar (posicionador de la pistola clavadora en el eje X)
  velocidadAuxiliar: 1200,
  aceleracionAuxiliar: 15000,
  pulsosPorCmAuxiliar: 100,
  inputAuxiliarManual: 1,
  invertirAuxiliar: false
};

// Correos con permisos de administrador. Al crear la cuenta se auto-aprueban y
// reciben isAdmin. Si la cuenta ya existía, hay que activarle el permiso desde
// el panel de administración: el permiso real es el campo isAdmin de Firestore,
// que es lo que comprueban las reglas de seguridad.
const ADMIN_EMAILS = ['rodolfomdelarocha@gmail.com', 'rodolfomz54@hotmail.com'];
const esCorreoAdmin = (correo) => ADMIN_EMAILS.includes(String(correo || '').trim().toLowerCase());

const Base = () => {
  const [manualOffset, setManualOffset] = useState(0);
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [modoClavado, setModoClavado] = useState(() => {
    return localStorage.getItem('modoClavado') === 'true';
  });
  const [posicionesEjecutadasClavado, setPosicionesEjecutadasClavado] = useState(() => {
    const saved = localStorage.getItem('posicionesEjecutadasClavado');
    return saved ? JSON.parse(saved) : [];
  });
  const [posicionActualClavado, setPosicionActualClavado] = useState(() => {
    const saved = localStorage.getItem('posicionActualClavado');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [modoClavadoIniciado, setModoClavadoIniciado] = useState(false);
  const [modoHiladoIniciado, setModoHiladoIniciado] = useState(false);
  const [modoTimelapse, setModoTimelapse] = useState(false);
  const [tiempoEsperaTimelapse, setTiempoEsperaTimelapse] = useState(() => {
    const saved = localStorage.getItem('tiempoEsperaTimelapse');
    return saved !== null ? parseInt(saved, 10) : 3;
  });
  // iMax: intervalo máximo de la curva de fotos (1 foto cada iMax hiladas al final)
  const [fotoIntervaloMax, setFotoIntervaloMax] = useState(() => {
    const saved = localStorage.getItem('fotoIntervaloMax');
    return saved !== null ? parseFloat(saved) : 10;
  });
  const [esperandoTimelapse, setEsperandoTimelapse] = useState(false);
  const [modoTikTok, setModoTikTok] = useState(false);
  const [modoTikTokIniciado, setModoTikTokIniciado] = useState(false);
  // Sección Puntos: imagen → nube de puntos (stippling) para clavar
  const [modoPuntos, setModoPuntos] = useState(() => {
    return FEATURE_PUNTOS && localStorage.getItem('modoPuntos') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('modoPuntos', modoPuntos);
  }, [modoPuntos]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    titulo: '',
    mensaje: '',
    onConfirm: () => {}
  });
  const [grosorTrazo, setGrosorTrazo] = useState(() => {
    const saved = localStorage.getItem('grosorTrazo');
    return saved !== null ? parseFloat(saved) : 0.05;
  });
  const [secuenciasRecientes, setSecuenciasRecientes] = useState([]);
  const [limiteSecuencias, setLimiteSecuencias] = useState(4);
  const [hayMasSecuencias, setHayMasSecuencias] = useState(false);
  const [uidVisualizando, setUidVisualizando] = useState(null);
  const [usuariosLista, setUsuariosLista] = useState([]);
  const [linkLoading, setLinkLoading] = useState({});   // { [secId]: true/false }
  const [linkCopiado, setLinkCopiado] = useState({});   // { [secId]: true/false }
  const [totalClavos, setTotalClavos] = useState(() => {
    const saved = localStorage.getItem('totalClavos');
    return saved !== null ? parseInt(saved, 10) : 250;
  });
  // Input de clavos como string libre: permite borrar todo mientras se escribe.
  // Se valida/clampa solo al salir del campo (blur).
  const [clavosInput, setClavosInput] = useState(String(totalClavos));
  useEffect(() => {
    setClavosInput(String(totalClavos));
  }, [totalClavos]);
  const commitClavos = () => {
    let v = parseInt(clavosInput, 10);
    if (isNaN(v)) v = totalClavos;
    v = Math.min(1000, Math.max(10, v));
    setTotalClavos(v);
    setClavosInput(String(v));
  };
  const [clavosRestantes, setClavosRestantes] = useState(() => {
    const saved = localStorage.getItem('clavosRestantes');
    return saved !== null ? parseInt(saved, 10) : 90;
  });
  const [errorClavosAgotados, setErrorClavosAgotados] = useState(false);
  const prevTotalClavosRef = useRef(totalClavos);
  const totalClavosRef = useRef(totalClavos);
  totalClavosRef.current = totalClavos;
  // Stats de ejecución para predecir tiempo restante.
  // Modelo: medimos por separado el tiempo entre pasos "sin foto" y "con foto",
  // y usamos la curva cuadrática del timelapse para predecir cuántos pasos restantes
  // serán con foto. Pausas no contaminan los promedios (lastMarkTime se resetea al pausar).
  const statsRunRef = useRef({
    inicio: null,
    puntosAlEmpezar: 0,
    lastMarkTime: null,
    lastMarkOutcome: null,
    tNoPhotoSum: 0,
    tNoPhotoCount: 0,
    tPhotoSum: 0,
    tPhotoCount: 0,
  });
  const timelapseNextRef = useRef(null);
  const timelapseTimerRef = useRef(null);
  // Acumulador para la frecuencia de fotos (curva cuadrática): cada hilada suma 1,
  // se toma foto cuando alcanza el intervalo(p) y se le resta ese intervalo.
  const fotoAccRef = useRef(0);
  // Ref síncrono: indica que estamos esperando el COMPLETADO del movimiento a 0.
  // Evita la condición de carrera de stateRef en el handler BLE.
  const timelapseMovingToCeroRef = useRef(false);

  // Hook de Bluetooth
  const {
    isConnected,
    isConnecting,
    error: bleError,
    deviceName,
    isBluetoothAvailable,
    isSecureContext,
    connect: connectBluetooth,
    disconnect: disconnectBluetooth,
    sendCommand,
    onStatusChange,
    logs // Extraer logs del hook
  } = useBluetooth();
  // Estados de secuencias
  const [secuenciaSeleccionada, setSecuenciaSeleccionada] = useState(null);
  const [showFormulario, setShowFormulario] = useState(false);
  
  // Inicializar Firestore en lugar de Realtime Database
  const db = getFirestore(app);
  
  const [conteoFallasHilo, setConteoFallasHilo] = useState(0);
  const [errorHiloRoto, setErrorHiloRoto] = useState(false);
  const [encoderLiveValue, setEncoderLiveValue] = useState(null);
  const [encoderHistory, setEncoderHistory] = useState([]);
  // Detección de hilo roto basada en pico del polling
  const encoderMaxCicloRef = useRef(0);   // máximo visto en el ciclo actual vía polling
  const encoderSospechososRef = useRef(0); // ciclos consecutivos por debajo del umbral
  // Estados de ejecución
  const [ejecutando, setEjecutando] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [posicionActualIndex, setPosicionActualIndex] = useState(0);
  const [posicionFisica, setPosicionFisica] = useState(0); // Posición absoluta imaginaria actual
  const [posicionesEjecutadas, setPosicionesEjecutadas] = useState([]);
  const [esperandoAccion, setEsperandoAccion] = useState(false);
  // Espejo síncrono de esperandoAccion. El estado de React se aplica con retraso
  // y stateRef se refresca en un useEffect, así que un evento del ESP32 que llegue
  // en pocos milisegundos puede leer un valor viejo. Pasa al hilar clavos
  // consecutivos: el propio hilado ya deja el carro en el clavo siguiente, el
  // movimiento dura cero y el aviso de completado llega antes de que React se
  // ponga al día, así que la secuencia creía seguir esperando y se detenía.
  // La lógica consulta esta ref; el estado queda solo para pintar la interfaz.
  const esperandoAccionRef = useRef(false);
  const fijarEsperandoAccion = (valor) => {
    esperandoAccionRef.current = valor;
    setEsperandoAccion(valor);
  };
  const [isLandscape, setIsLandscape] = useState(true);
  
  // Estado de configuración de motores con inicialización por defecto (sin localStorage)
  const [motorConfig, setMotorConfig] = useState(MOTOR_CONFIG_DEFAULTS);
  const [configCargada, setConfigCargada] = useState(false); // Flag para evitar sobrescribir con defaults localmente 
  const [userData, setUserData] = useState(null); // Datos del perfil de Firestore
  const [showAdminPanel, setShowAdminPanel] = useState(false); // Mostrar modal admin
  const [sidebarAbierto, setSidebarAbierto] = useState(false); // Sidebar lateral
  const [showHomingModal, setShowHomingModal] = useState(false); // Modal de calibración de posición cero
  const [configSavedNotif, setConfigSavedNotif] = useState(false); // Toast de config guardada


  // --- AUTHENTICATION STATE ---
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const puedeUsarTikTok = FEATURE_TIKTOK && (userData?.isAdmin === true || esCorreoAdmin(user?.email) || userData?.canUseTikTok === true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
      if (currentUser) {
        setUidVisualizando(currentUser.uid);
      } else {
        setUidVisualizando(null);
        setUsuariosLista([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // --- CONFIG SYNC WITH FIRESTORE ---
  // Cargar config al iniciar sesión
  useEffect(() => {
    if (user) {
      const docRef = doc(db, "users", user.uid);
      
      // Usamos onSnapshot para detectar cambios en tiempo real (ej: si te aprueban)
      const unsubscribeUser = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          
          // --- NUEVO: Asegurar que el email esté en el documento ---
          if (!data.email && user.email) {
            updateDoc(docRef, { email: user.email }).catch(err => console.error("Error actualizando email:", err));
          }
          
          // Solo cargamos la config si aún no está cargada para evitar bucles raros,
          // aunque el estado configCargada ya ayuda a eso.
          if (!configCargada && data.motorConfig) {
             console.log("Cargando configuración remota...");
             setMotorConfig(prev => ({...prev, ...data.motorConfig}));
             setConfigCargada(true);
          } else if (!configCargada) {
             setConfigCargada(true); // Existe usuario pero sin config? Raro, pero posible.
          }
        } else {
          console.log("ℹ️ Nuevo usuario, registrando defaults y estado PENDIENTE...");
          // Si el usuario es nuevo, creamos su documento con los defaults
          const initialData = { 
            motorConfig: MOTOR_CONFIG_DEFAULTS,
            createdAt: new Date(),
            email: user.email,
            estado: esCorreoAdmin(user.email) ? 'aprobado' : 'pendiente', // Auto-aprobar al admin
            isAdmin: esCorreoAdmin(user.email)
          };
          
          setDoc(docRef, initialData, { merge: true })
          .then(() => {
            console.log("✅ Usuario registrado correctamente");
            setUserData(initialData); // Seteamos localmente para no esperar al snapshot
            setConfigCargada(true);
          })
          .catch(err => {
            console.error("Error al registrar usuario:", err);
            setConfigCargada(true);
          });
        }
      }, (error) => {
        console.error("Error en snapshot de usuario:", error);
        setConfigCargada(true);
      });
      
      return () => unsubscribeUser();
    }
  }, [user, db, configCargada]);

  // Guardar config al cambiar (Solo si ya se cargó la configuración remota)
  useEffect(() => {
    if (user && motorConfig && configCargada) {
      const docRef = doc(db, "users", user.uid);
      // Usamos setDoc con merge para no borrar otros datos del usuario si los hubiera
      setDoc(docRef, { motorConfig }, { merge: true })
        .then(() => console.log("Configuración sincronizada en la nube"))
        .catch(err => console.error("Error guardando config remota:", err));

      // --- NUEVO: Enviar configuración al ESP32 vía Bluetooth ---
      // Esto asegura que si cambiamos "invertir", se aplique al instante
      if (isConnected) {
        sendCommand('CONFIG_MOTORES', {
          invertirPrincipal: motorConfig.invertirPrincipal || false,
          invertirHilador: motorConfig.invertirHilador || false,
          invertirElevador: motorConfig.invertirElevador || false,
          invertirAuxiliar: motorConfig.invertirAuxiliar || false,
          invertirRele: motorConfig.invertirRele || false
        });
      }
    }
  }, [motorConfig, user, db, configCargada, isConnected]);


  // --- CARGA DE DATOS GLOBAL (Tiempo Real) ---
  // Mantener las secuencias siempre listas para la galería y dropdowns
  useEffect(() => {
    if (!user || !uidVisualizando) {
      setSecuenciasRecientes([]);
      return;
    }

    const q = query(
      collection(db, 'secuencias_automaticas'), 
      where('uid', '==', uidVisualizando),
      orderBy('createdAt', 'desc'),
      limit(limiteSecuencias + 1)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs;
      const hayMas = docs.length > limiteSecuencias;
      const lista = docs.slice(0, limiteSecuencias).map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setSecuenciasRecientes(lista);
      setHayMasSecuencias(hayMas);
    }, (error) => {
      console.error("Error en onSnapshot superior:", error);
    });

    return () => unsubscribe();
  }, [db, user, uidVisualizando, limiteSecuencias]);

  // Cargar lista de usuarios para el dropdown del admin
  useEffect(() => {
    const esAdmin = userData?.isAdmin === true || esCorreoAdmin(user?.email);
    if (!user || !esAdmin) {
      setUsuariosLista([]);
      return;
    }
    getDocs(query(collection(db, 'users'), orderBy('email')))
      .then(snapshot => {
        const lista = snapshot.docs.map(d => ({ id: d.id, email: d.data().email || d.id }));
        setUsuariosLista(lista);
      })
      .catch(err => console.error('Error cargando usuarios para dropdown:', err));
  }, [db, user, userData]);

  // Persistencia para el modo clavado
  useEffect(() => {
    localStorage.setItem('modoClavado', modoClavado);
    localStorage.setItem('posicionesEjecutadasClavado', JSON.stringify(posicionesEjecutadasClavado));
    localStorage.setItem('posicionActualClavado', posicionActualClavado);
    localStorage.setItem('clavosRestantes', clavosRestantes);
    localStorage.setItem('grosorTrazo', grosorTrazo);
    localStorage.setItem('totalClavos', totalClavos);
    localStorage.setItem('tiempoEsperaTimelapse', tiempoEsperaTimelapse);
    localStorage.setItem('fotoIntervaloMax', fotoIntervaloMax);
  }, [modoClavado, posicionesEjecutadasClavado, posicionActualClavado, clavosRestantes, grosorTrazo, totalClavos, tiempoEsperaTimelapse, fotoIntervaloMax]);

  // Ya no necesitamos el useEffect de carga inicial separado
  
  // Detección de orientación
  useEffect(() => {
    const checkOrientation = () => {
      // Priorizar API moderna, fallback a dimensiones
      if (window.screen?.orientation?.type) {
        setIsLandscape(window.screen.orientation.type.includes('landscape'));
      } else {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  
  // Resetear desfase al desconectar
  useEffect(() => {
    if (!isConnected && manualOffset !== 0) {
      alert("⚠️ Desfase perdido por desconexión. Debes recalibrar.");
      setManualOffset(0);
    }
  }, [isConnected, manualOffset]);

  // WAKE LOCK: Mantener pantalla encendida
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          console.log('💡 Wake Lock activo: La pantalla no se apagará.');
        } else {
          console.warn('⚠️ Wake Lock API no soportada en este navegador.');
        }
      } catch (err) {
        console.error(`❌ Error al solicitar Wake Lock: ${err.name}, ${err.message}`);
      }
    };

    // Solicitar al montar
    requestWakeLock();

    // Re-solicitar si la visibilidad cambia (ej. minimizar y volver)
    const handleVisibilityChange = () => {
      if (wakeLock !== null && document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock !== null) wakeLock.release();
    };
  }, []);

  // Refs para evitar loops de renderizado
  const stateRef = useRef({
    ejecutando,
    pausado,
    posicionActualIndex,
    secuenciaSeleccionada,
    posicionesEjecutadas,
    posicionFisica,
    esperandoAccion,
    motorConfig,
    manualOffset,
    conteoFallasHilo,
    errorHiloRoto,
    modoClavado,
    posicionesEjecutadasClavado,
    posicionActualClavado,
    modoTikTok,
    totalClavos,
    modoTimelapse,
    tiempoEsperaTimelapse,
    esperandoTimelapse,
    fotoIntervaloMax
  });

  // Mantener ref sincronizada
  useEffect(() => {
    stateRef.current = {
      ejecutando,
      pausado,
      posicionActualIndex,
      secuenciaSeleccionada,
      posicionesEjecutadas,
      posicionFisica,
      esperandoAccion,
      motorConfig,
      manualOffset,
      conteoFallasHilo,
      errorHiloRoto,
      modoClavado,
      posicionesEjecutadasClavado,
      posicionActualClavado,
      modoTikTok,
      totalClavos,
      modoTimelapse,
      tiempoEsperaTimelapse,
      esperandoTimelapse,
      fotoIntervaloMax
    };
  }, [ejecutando, pausado, posicionActualIndex, secuenciaSeleccionada, posicionesEjecutadas, posicionFisica, esperandoAccion, motorConfig, manualOffset, conteoFallasHilo, errorHiloRoto, modoClavado, posicionesEjecutadasClavado, posicionActualClavado, modoTikTok, totalClavos, modoTimelapse, tiempoEsperaTimelapse, esperandoTimelapse, fotoIntervaloMax]);

  // ── CONTROL REMOTO ────────────────────────────────────────────────────────
  // sessionId único de esta pestaña/dispositivo. Persiste durante la vida del componente.
  const sessionIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const [remoteEstado, setRemoteEstado] = useState(null);
  const [bateria, setBateria] = useState(null); // { level: 0-100, charging: bool } | null

  // Batería de la tablet (Battery API — Chrome/Android. iOS Safari no soporta.)
  useEffect(() => {
    if (!navigator.getBattery) return;
    let battObj = null;
    let mounted = true;
    const update = () => {
      if (!battObj || !mounted) return;
      setBateria({ level: Math.round(battObj.level * 100), charging: battObj.charging });
    };
    navigator.getBattery().then(b => {
      battObj = b;
      update();
      b.addEventListener('levelchange', update);
      b.addEventListener('chargingchange', update);
    }).catch(() => {});
    return () => {
      mounted = false;
      if (battObj) {
        battObj.removeEventListener('levelchange', update);
        battObj.removeEventListener('chargingchange', update);
      }
    };
  }, []);
  const [remoteEstadoCargado, setRemoteEstadoCargado] = useState(false);
  const ultimoComandoEjecutadoRef = useRef(null);
  // Momento (reloj local) en que llegó el último snapshot del primary. Se usa
  // para caducar el aviso sin depender del reloj del servidor ni del de la tablet.
  const [remoteRecibidoEn, setRemoteRecibidoEn] = useState(0);

  // Suscripción a controlRemoto/{uid} — siempre activa cuando hay user
  useEffect(() => {
    if (!user) {
      setRemoteEstado(null);
      setRemoteEstadoCargado(false);
      return;
    }
    const ref = doc(db, 'controlRemoto', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      setRemoteEstado(snap.exists() ? snap.data() : null);
      setRemoteRecibidoEn(Date.now());
      setRemoteEstadoCargado(true);
    }, (err) => {
      console.error('Error snapshot controlRemoto:', err);
      setRemoteEstadoCargado(true);
    });
    return () => unsub();
  }, [user, db]);

  // Preferencia de este dispositivo: forzar la app completa aunque otra sesión
  // tenga el BLE. Útil en la computadora para gestionar secuencias/config sin
  // quedar atrapado en el control remoto. Se recuerda entre reinicios.
  const [preferirAppCompleta, setPreferirAppCompleta] = useState(() => {
    return localStorage.getItem('preferirAppCompleta') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('preferirAppCompleta', preferirAppCompleta);
  }, [preferirAppCompleta]);

  // Preferencia de este dispositivo: forzar el control remoto aunque NO se haya
  // detectado como teléfono (la detección teléfono/tablet no es fiable). Útil
  // cuando un celular abre la app completa por error. Se recuerda entre reinicios.
  const [preferirControlRemoto, setPreferirControlRemoto] = useState(() => {
    return localStorage.getItem('preferirControlRemoto') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('preferirControlRemoto', preferirControlRemoto);
  }, [preferirControlRemoto]);

  // Las dos preferencias son mutuamente excluyentes: elegir una limpia la otra.
  const usarControlRemoto = () => {
    setPreferirAppCompleta(false);
    setPreferirControlRemoto(true);
  };
  const usarAppCompleta = () => {
    setPreferirControlRemoto(false);
    setPreferirAppCompleta(true);
  };

  // Heurística de teléfono: por defecto el celular abre el control remoto y la
  // tablet/PC abre la app completa. Como es difícil distinguir un teléfono de una
  // tablet, esto es solo un default; el usuario puede forzar cualquier modo.
  const esTelefono = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const uaTelefono = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const w = window.screen?.width || window.innerWidth || 0;
    const h = window.screen?.height || window.innerHeight || 0;
    const ladoCorto = Math.min(w, h);
    // Teléfono: UA de teléfono, o pantalla con lado corto pequeño (≈ móvil).
    return uaTelefono || (ladoCorto > 0 && ladoCorto < 480);
  }, []);

  // El primary late cada LATIDO_MS; si pasan más de LATIDO_CADUCA_MS sin recibir
  // nada se da por desaparecido (app cerrada de golpe, tablet suspendida, sin
  // red…). Así el aviso de "otra sesión controla la máquina" no se queda fijo
  // por un documento que nadie llegó a apagar.
  const LATIDO_MS = 15000;
  const LATIDO_CADUCA_MS = 45000;
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);
  // Un documento con updatedAt de hace más de 10 min es un primary muerto: se
  // descarta al instante, sin esperar la caducidad del latido. El margen tan
  // amplio evita falsos positivos por desfase del reloj del dispositivo.
  const updatedMs = remoteEstado?.updatedAt?.toMillis?.() ?? null;
  const docMuyViejo = updatedMs != null && (ahora - updatedMs) > 10 * 60 * 1000;
  const primaryConLatido = !docMuyViejo && remoteRecibidoEn > 0 && (ahora - remoteRecibidoEn) < LATIDO_CADUCA_MS;

  // Hay otra sesión (tablet) controlando físicamente la máquina vía BLE.
  const hayOtraSesionPrimary = !!(
    user &&
    remoteEstado &&
    remoteEstado.activo === true &&
    remoteEstado.sessionId &&
    remoteEstado.sessionId !== sessionIdRef.current &&
    primaryConLatido
  );

  // Determina si esta sesión actúa como REMOTA (control remoto en vez de app completa).
  // Se entra en control remoto cuando:
  //   · el usuario lo forzó explícitamente en este dispositivo, o
  //   · es un teléfono y no está conectado al BLE de la máquina, o
  //   · otra sesión (tablet) ya tiene el BLE de la máquina.
  // En todos los casos, elegir "app completa" en este equipo tiene prioridad.
  const isRemoteMode =
    !preferirAppCompleta &&
    !isConnected &&
    (preferirControlRemoto || esTelefono || hayOtraSesionPrimary);

  // Publicación de estado: esta sesión es PRIMARY cuando hay BLE conectado.
  // Escribe el documento con sessionId propio y mantiene actualizado el estado en tiempo real.
  useEffect(() => {
    if (!user || !isConnected || isRemoteMode) return;
    const ref = doc(db, 'controlRemoto', user.uid);

    const modo = modoClavado ? 'clavado' : (modoTikTok ? 'tiktok' : 'hilado');
    const posicionesSec = modoClavado
      ? Array.from({ length: totalClavos }, (_, i) => i)
      : (secuenciaSeleccionada?.posiciones || []);
    const totalPos = modoClavado ? totalClavos : (secuenciaSeleccionada?.posiciones?.length || 0);
    const idxActual = modoClavado ? posicionActualClavado : posicionActualIndex;
    const ejecutadas = modoClavado ? posicionesEjecutadasClavado : posicionesEjecutadas;

    const payload = {
      activo: true,
      sessionId: sessionIdRef.current,
      bleConectado: true,
      modo,
      secuenciaId: secuenciaSeleccionada?.id || null,
      secuenciaNombre: secuenciaSeleccionada?.nombre || (modoClavado ? 'Clavado Perimetral' : ''),
      posiciones: posicionesSec,
      totalPosiciones: totalPos,
      posicionActualIndex: idxActual,
      totalClavos,
      ejecutando,
      pausado,
      esperandoTimelapse,
      tiempoRestante,
      clavosRestantes,
      alertas: {
        hiloRoto: errorHiloRoto,
        clavosAgotados: errorClavosAgotados,
      },
      bateria,
      updatedAt: serverTimestamp(),
    };
    setDoc(ref, payload, { merge: true }).catch(err => console.error('Error publicando estado remoto:', err));
  }, [
    user, db, isConnected, isRemoteMode,
    secuenciaSeleccionada, modoClavado, modoTikTok,
    posicionActualIndex, posicionActualClavado,
    posicionesEjecutadas, posicionesEjecutadasClavado,
    ejecutando, pausado, esperandoTimelapse,
    errorHiloRoto, errorClavosAgotados,
    tiempoRestante, clavosRestantes, totalClavos,
    bateria
  ]);

  // Latido: mientras esta sesión sea primary, refresca updatedAt aunque no
  // cambie nada, para que las demás sesiones sepan que sigue viva.
  useEffect(() => {
    if (!user || !isConnected || isRemoteMode) return;
    const ref = doc(db, 'controlRemoto', user.uid);
    const id = setInterval(() => {
      updateDoc(ref, { activo: true, updatedAt: serverTimestamp() }).catch(() => {});
    }, LATIDO_MS);
    return () => clearInterval(id);
  }, [user, db, isConnected, isRemoteMode]);

  // Al perder el BLE, esta sesión deja de ser primary de inmediato: antes solo
  // se apagaba al cerrar la pestaña, y una desconexión dejaba el aviso fijo.
  const eraPrimaryRef = useRef(false);
  useEffect(() => {
    if (isConnected && !isRemoteMode) { eraPrimaryRef.current = true; return; }
    if (!isConnected && eraPrimaryRef.current) {
      eraPrimaryRef.current = false;
      if (user && remoteEstado?.sessionId === sessionIdRef.current) {
        updateDoc(doc(db, 'controlRemoto', user.uid), {
          activo: false, bleConectado: false, updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
    }
  }, [isConnected, isRemoteMode, user, db, remoteEstado?.sessionId]);

  // Al desmontar / cerrar pestaña: marcar inactivo (solo si esta sesión era primary).
  // pagehide cubre iOS/Android, donde beforeunload no siempre se dispara.
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'controlRemoto', user.uid);
    const apagar = () => {
      // Solo si yo soy el sessionId actual
      if (remoteEstado?.sessionId === sessionIdRef.current) {
        // updateDoc es fire-and-forget en beforeunload; suele llegar
        updateDoc(ref, { activo: false, bleConectado: false }).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', apagar);
    window.addEventListener('pagehide', apagar);
    return () => {
      window.removeEventListener('beforeunload', apagar);
      window.removeEventListener('pagehide', apagar);
      apagar();
    };
  }, [user, db, remoteEstado?.sessionId]);

  // El aviso se puede cerrar; vuelve a salir solo si aparece un primary distinto.
  const [avisoSesionOcultoPara, setAvisoSesionOcultoPara] = useState(null);

  // ── VIDEO STREAMING (TABLET → TELÉFONO, solo admin) ─────────────────────
  const pcVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const videoUnsubsRef = useRef([]);
  const videoSessionActivaRef = useRef(null);
  const [videoTransmitiendo, setVideoTransmitiendo] = useState(false);
  const [videoError, setVideoError] = useState(null);

  const esAdminFlag = userData?.isAdmin === true || esCorreoAdmin(user?.email);

  const stopVideoSession = React.useCallback(() => {
    videoUnsubsRef.current.forEach(u => { try { u(); } catch (e) {} });
    videoUnsubsRef.current = [];
    if (pcVideoRef.current) {
      try { pcVideoRef.current.close(); } catch (e) {}
      pcVideoRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    videoSessionActivaRef.current = null;
    setVideoTransmitiendo(false);
    if (user) {
      updateDoc(doc(db, 'controlRemoto', user.uid), {
        'video.activo': false,
        'video.offer': null,
        'video.answer': null
      }).catch(() => {});
    }
  }, [user, db]);

  const startVideoSession = React.useCallback(async (sessionId) => {
    videoSessionActivaRef.current = sessionId;
    setVideoError(null);

    try {
      // 1. Cámara (trasera, baja resolución, sin audio)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 15, max: 20 }
        },
        audio: false
      });
      localStreamRef.current = stream;

      // 2. Peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcVideoRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      // 3. Limpiar ICE de sesiones anteriores
      const iceTabletRef = collection(db, 'controlRemoto', user.uid, 'videoIceTablet');
      const icePhoneRef = collection(db, 'controlRemoto', user.uid, 'videoIcePhone');
      const [oldT, oldP] = await Promise.all([getDocs(iceTabletRef), getDocs(icePhoneRef)]);
      await Promise.all([...oldT.docs, ...oldP.docs].map(d => deleteDoc(d.ref)));

      // 4. Subir nuestros ICE
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          addDoc(iceTabletRef, { sessionId, candidate: e.candidate.toJSON() }).catch(() => {});
        }
      };
      pc.onconnectionstatechange = () => {
        const st = pc.connectionState;
        if (st === 'connected') setVideoTransmitiendo(true);
        if (st === 'failed' || st === 'disconnected' || st === 'closed') {
          if (videoSessionActivaRef.current === sessionId) stopVideoSession();
        }
      };

      // 5. Oferta
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const docRef = doc(db, 'controlRemoto', user.uid);
      await updateDoc(docRef, {
        'video.activo': true,
        'video.offer': { type: offer.type, sdp: offer.sdp },
        'video.answer': null
      });
      setVideoTransmitiendo(true);

      // 6. Escuchar answer del teléfono
      const unsubDoc = onSnapshot(docRef, async (snap) => {
        const d = snap.data();
        if (d?.video?.sessionId !== sessionId) return;
        const answer = d?.video?.answer;
        if (answer && !pc.currentRemoteDescription) {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(answer));
          } catch (e) { console.error('Error setRemoteDesc:', e); }
        }
      });

      // 7. Escuchar ICE candidates del teléfono
      const unsubIce = onSnapshot(icePhoneRef, (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'added') {
            const d = change.doc.data();
            if (d.sessionId !== sessionId) return;
            pc.addIceCandidate(new RTCIceCandidate(d.candidate)).catch(() => {});
          }
        });
      });

      videoUnsubsRef.current = [unsubDoc, unsubIce];
    } catch (err) {
      console.error('Error iniciando video session:', err);
      setVideoError(err.message || String(err));
      stopVideoSession();
    }
  }, [user, db, stopVideoSession]);

  // Effect: arrancar/parar según la solicitud del teléfono
  useEffect(() => {
    const shouldRun = !isRemoteMode && user && esAdminFlag && isConnected;
    const video = remoteEstado?.video;
    const solicitado = !!video?.solicitado;
    const sessionId = video?.sessionId;

    if (!shouldRun || !solicitado || !sessionId) {
      if (videoSessionActivaRef.current) stopVideoSession();
      return;
    }
    if (videoSessionActivaRef.current === sessionId) return;
    if (videoSessionActivaRef.current) stopVideoSession();
    startVideoSession(sessionId);
  }, [remoteEstado?.video?.solicitado, remoteEstado?.video?.sessionId, isRemoteMode, user, esAdminFlag, isConnected, startVideoSession, stopVideoSession]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (videoSessionActivaRef.current) stopVideoSession();
    };
  }, [stopVideoSession]);

  // Ejecutar comandos recibidos (cuando esta sesión es primary)
  useEffect(() => {
    if (!user || isRemoteMode) return;
    const cmd = remoteEstado?.comando;
    if (!cmd || !cmd.id) return;
    if (cmd.id === ultimoComandoEjecutadoRef.current) return;
    if (cmd.id === remoteEstado?.ultimoComandoProcesadoId) return;

    ultimoComandoEjecutadoRef.current = cmd.id;

    // Ejecutar la acción
    switch (cmd.accion) {
      case 'pausar':
        if (ejecutando && !pausado) {
          setPausado(true);
          if (timelapseTimerRef.current) {
            clearTimeout(timelapseTimerRef.current);
            timelapseTimerRef.current = null;
          }
        }
        break;
      case 'reanudar':
        if (modoClavado) {
          if (pausado) {
            setPausado(false);
            setEjecutando(true);
            ejecutarEnPosicionActualRef(stateRef.current);
          } else if (!ejecutando) {
            statsRunRef.current = { inicio: Date.now(), puntosAlEmpezar: posicionesEjecutadasClavado.length };
            setEjecutando(true);
            setPausado(false);
            setTimeout(() => ejecutarEnPosicionActualRef(stateRef.current), 100);
          }
        } else if (secuenciaSeleccionada) {
          if (pausado) {
            setPausado(false);
            setEjecutando(true);
            if (esperandoTimelapse && timelapseNextRef.current) {
              iniciarEsperaTimelapse(tiempoEsperaTimelapse);
            } else {
              ejecutarEnPosicionActualRef(stateRef.current);
            }
          } else if (!ejecutando) {
            statsRunRef.current = { inicio: Date.now(), puntosAlEmpezar: posicionesEjecutadas.length };
            fotoAccRef.current = 0;
            setEjecutando(true);
            setPausado(false);
            setTimeout(() => ejecutarEnPosicionActualRef(stateRef.current), 100);
          }
        }
        break;
      case 'reparado':
        handleReparado();
        break;
      case 'recargarClavos':
        handleRecargarClavos();
        break;
      default:
        console.warn('Comando remoto desconocido:', cmd.accion);
    }

    // Ack: registrar el id procesado
    const ref = doc(db, 'controlRemoto', user.uid);
    updateDoc(ref, { ultimoComandoProcesadoId: cmd.id }).catch(err => console.error('Error ack comando:', err));
  }, [remoteEstado?.comando, isRemoteMode, user, db]);

  // Función para formatear fechas relativas cortas
  const formatRelativeDate = (date) => {
    if (!date) return "Recién creado";
    // Manejar tanto timestamps de Firebase como objetos Date normales
    const dt = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    
    // Resetear horas para comparar solo días
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    
    const diffInMs = startOfToday - startOfDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Hoy";
    if (diffInDays === 1) return "Ayer";
    if (diffInDays === 2) return "Antier";
    if (diffInDays < 7) return `Hace ${diffInDays} días`;
    if (diffInDays < 30) return `Hace ${Math.floor(diffInDays / 7)} sem.`;
    if (diffInDays < 365) return `Hace ${Math.floor(diffInDays / 30)} meses`;
    return `Hace ${Math.floor(diffInDays / 365)} años`;
  };

  const RETRATO_BASE_URL = 'https://app.rocharamas.com';

  const generarLink = async (sec) => {
    setLinkLoading(prev => ({ ...prev, [sec.id]: true }));
    try {
      const retratoRef = await addDoc(collection(db2, 'retratos'), {
        nombre: sec.nombre,
        secuencia: sec.posiciones,
        ultimaPosicion: 0,
      });

      await updateDoc(doc(db, 'secuencias_automaticas', sec.id), {
        linkGenerado: retratoRef.id,
      });
    } catch (e) {
      console.error('Error al generar link:', e);
    } finally {
      setLinkLoading(prev => ({ ...prev, [sec.id]: false }));
    }
  };

  const copiarLink = async (secId, linkId) => {
    const url = `${RETRATO_BASE_URL}/?id=${linkId}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopiado(prev => ({ ...prev, [secId]: true }));
      setTimeout(() => setLinkCopiado(prev => ({ ...prev, [secId]: false })), 2000);
    } catch {
      prompt('Copia el link manualmente:', url);
    }
  };

  const [resetModalSec, setResetModalSec] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const confirmarReset = async () => {
    if (!resetModalSec) return;
    setResetLoading(true);
    try {
      await updateDoc(doc(db, 'secuencias_automaticas', resetModalSec.id), {
        ultimaPosicion: 0,
        posicionesEjecutadas: [],
      });
      setResetModalSec(null);
    } catch (err) {
      console.error('Error al reiniciar progreso:', err);
    } finally {
      setResetLoading(false);
    }
  };

  // ── REASIGNAR SECUENCIA A OTRO USUARIO (solo admin) ──────────────────────
  // El admin puede mover una secuencia (imagen/retrato) al usuario que elija,
  // cambiando el campo uid del documento para que le aparezca a esa persona.
  const [reasignarSec, setReasignarSec] = useState(null);   // secuencia a reasignar
  const [reasignarTarget, setReasignarTarget] = useState('');
  const [reasignarLoading, setReasignarLoading] = useState(false);

  // Menú contextual (kebab) abierto en una tarjeta: guarda el id de la secuencia.
  const [menuSecId, setMenuSecId] = useState(null);
  // Cerrar el menú al hacer click en cualquier parte fuera de él.
  useEffect(() => {
    if (!menuSecId) return;
    const cerrar = () => setMenuSecId(null);
    window.addEventListener('click', cerrar);
    return () => window.removeEventListener('click', cerrar);
  }, [menuSecId]);

  const abrirReasignar = (sec) => {
    setReasignarSec(sec);
    setReasignarTarget(sec.uid || '');
  };

  const confirmarReasignar = async () => {
    if (!reasignarSec || !reasignarTarget) return;
    setReasignarLoading(true);
    try {
      await updateDoc(doc(db, 'secuencias_automaticas', reasignarSec.id), {
        uid: reasignarTarget,
      });
      // Si estoy viendo las secuencias del usuario destino, seguirá visible;
      // si no, desaparecerá de la lista actual (cambió de dueño).
      setReasignarSec(null);
      setReasignarTarget('');
    } catch (err) {
      console.error('Error al reasignar secuencia:', err);
    } finally {
      setReasignarLoading(false);
    }
  };

  // Auto-detectar totalClavos desde la secuencia de hilado
  useEffect(() => {
    if (!modoClavado && secuenciaSeleccionada?.posiciones?.length > 0) {
      const maxPos = Math.max(...secuenciaSeleccionada.posiciones);
      const detectado = maxPos + 1;
      setTotalClavos(detectado);
    }
  }, [secuenciaSeleccionada?.id, modoClavado]);

  // Reconvertir posicionFisica cuando cambia totalClavos para mantener la posición real del motor
  useEffect(() => {
    const prev = prevTotalClavosRef.current;
    if (prev !== totalClavos && prev > 0) {
      setPosicionFisica(pf => {
        const pulsosReales = pf * (4000 / prev);
        return pulsosReales / (4000 / totalClavos);
      });
    }
    prevTotalClavosRef.current = totalClavos;
  }, [totalClavos]);

  // Sincronizar solo la secuencia seleccionada si existe
  useEffect(() => {
    if (!secuenciaSeleccionada?.id || secuenciaSeleccionada.id === 'prueba') return;

    const unsubscribe = onSnapshot(doc(db, 'secuencias_automaticas', secuenciaSeleccionada.id), (docSnap) => {
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setSecuenciaSeleccionada(data);
      }
    }, (error) => {
      console.error("Error sincronizando secuencia seleccionada:", error);
    });
    
    return () => unsubscribe();
  }, [db, secuenciaSeleccionada?.id]);
  
  // Guardar progreso en Firestore
  useEffect(() => {
    if (secuenciaSeleccionada && secuenciaSeleccionada.id && secuenciaSeleccionada.id !== 'prueba') {
      const docRef = doc(db, 'secuencias_automaticas', secuenciaSeleccionada.id);
      updateDoc(docRef, {
        ultimaPosicion: posicionActualIndex,
        posicionesEjecutadas: posicionesEjecutadas
      }).catch(err => console.error("Error guardando progreso:", err));
    }
  }, [posicionActualIndex, posicionesEjecutadas, secuenciaSeleccionada?.id, db]);
  
  // Funciones puras de cálculo
  const calcularPosicionAbsolutaPura = (posicionObjetivo, posicionFisicaActual) => {
    const objetivo = parseInt(posicionObjetivo, 10);
    
    const tc = totalClavosRef.current;
    const pasosPorVuelta = 4000;
    const pasosPorPosicion = pasosPorVuelta / tc;

    const pasosActuales = convertirAPulsos(posicionFisicaActual);
    const pasosObjetivoBase = objetivo * pasosPorPosicion;

    const diffEnPasos = pasosObjetivoBase - (pasosActuales % pasosPorVuelta);

    let pasosFinales = pasosActuales + diffEnPasos;

    if (diffEnPasos > pasosPorVuelta / 2) {
      pasosFinales -= pasosPorVuelta;
    } else if (diffEnPasos < -pasosPorVuelta / 2) {
      pasosFinales += pasosPorVuelta;
    }

    return pasosFinales / pasosPorPosicion;
  };

  const convertirAPulsos = (posicionAbsoluta) => {
    return Math.round(posicionAbsoluta * (4000 / totalClavosRef.current));
  };

  // Inicia la pausa de timelapse en posición 0: dispara la cámara y, tras X segundos,
  // avanza a la siguiente posición. Usa timelapseNextRef (síncrono) como fuente de verdad.
  const iniciarEsperaTimelapse = (tiempoSeg) => {
    // Disparar la cámara del teléfono (HID) tras dejar que se asienten las vibraciones
    setTimeout(() => sendCommand('TOMAR_FOTO'), 800);
    timelapseTimerRef.current = setTimeout(() => {
      timelapseTimerRef.current = null;
      setEsperandoTimelapse(false);
      const next = timelapseNextRef.current;
      timelapseNextRef.current = null;
      if (next) avanzarASiguientePosicionRef(next.currentState, next.nuevasEjecutadas);
    }, (tiempoSeg || 3) * 1000);
  };

  // Funciones ref-aware para el callback
  const marcarPosicionComoEjecutadaRef = (currentState) => {
    const { 
      posicionesEjecutadas, 
      posicionActualIndex, 
      secuenciaSeleccionada,
      modoClavado,
      posicionesEjecutadasClavado,
      posicionActualClavado
    } = currentState;

    // Registrar intervalo del paso anterior (atribuir al outcome previo).
    // El outcome del paso actual se setea más abajo según el flujo.
    const stats = statsRunRef.current;
    const ahoraMark = Date.now();
    if (stats.lastMarkTime !== null && stats.lastMarkOutcome) {
      const delta = ahoraMark - stats.lastMarkTime;
      // Ignorar outliers (pausas largas, freezes): >5 min
      if (delta > 0 && delta < 5 * 60 * 1000) {
        if (stats.lastMarkOutcome === 'photo') {
          stats.tPhotoSum += delta;
          stats.tPhotoCount += 1;
        } else {
          stats.tNoPhotoSum += delta;
          stats.tNoPhotoCount += 1;
        }
      }
    }
    stats.lastMarkTime = ahoraMark;

    if (modoClavado) {
      const nuevasEjecutadas = [...posicionesEjecutadasClavado, posicionActualClavado];
      setPosicionesEjecutadasClavado(nuevasEjecutadas);
      stats.lastMarkOutcome = 'noPhoto';
      setTiempoRestante(predecirTiempoRestante(currentState, nuevasEjecutadas, totalClavos));
      avanzarASiguientePosicionRef({ ...currentState, posicionesEjecutadasClavado: nuevasEjecutadas }, nuevasEjecutadas);
      return;
    }

    const nuevasEjecutadas = [...posicionesEjecutadas, posicionActualIndex];
    setPosicionesEjecutadas(nuevasEjecutadas);
    const totalSec = secuenciaSeleccionada?.posiciones?.length || 0;
    setTiempoRestante(predecirTiempoRestante(currentState, nuevasEjecutadas, totalSec));

    // En modo TikTok: parar después de cada posición (el panel gestiona la cola)
    if (currentState.modoTikTok) {
      setEjecutando(false);
      fijarEsperandoAccion(false);
      return;
    }

    // Modo Timelapse: solo en los pasos donde "toca foto" se desvía a 0 y espera.
    // La frecuencia de fotos sigue una curva cuadrática: muchas al inicio, pocas al final.
    if (currentState.modoTimelapse && secuenciaSeleccionada?.herramienta === 'hilador') {
      const totalPosiciones = secuenciaSeleccionada.posiciones.length;
      const progreso = Math.min(1, nuevasEjecutadas.length / totalPosiciones);
      const intervaloMax = Math.max(1, currentState.fotoIntervaloMax || 10); // configurable (iMax)
      const intervalo = 1 + (intervaloMax - 1) * progreso * progreso; // cuadrática suave

      fotoAccRef.current += 1;
      const tocaFoto = fotoAccRef.current >= intervalo;
      if (tocaFoto) fotoAccRef.current -= intervalo;

      // Registrar outcome del paso actual para el siguiente cálculo de intervalo
      stats.lastMarkOutcome = tocaFoto ? 'photo' : 'noPhoto';

      // Si no toca foto: hilar de corrido, avanzar directo sin ir a 0 (mucho más rápido)
      if (!tocaFoto) {
        avanzarASiguientePosicionRef(currentState, nuevasEjecutadas);
        return;
      }

      const posicionAbsolutaCero = calcularPosicionAbsolutaPura(0, currentState.posicionFisica);
      const pulsosCero = convertirAPulsos(posicionAbsolutaCero) + currentState.manualOffset;
      const pulsosActualesTL = convertirAPulsos(currentState.posicionFisica) + currentState.manualOffset;

      setPosicionFisica(posicionAbsolutaCero);
      setEsperandoTimelapse(true); // solo para el indicador visual

      // IMPORTANTE: tras mover a 0, la máquina está físicamente en 0. El avance posterior
      // debe calcular la ruta desde 0, no desde la posición vieja de hilado. Guardamos el
      // estado con posicionFisica corregida para que el cálculo de ruta más corta sea correcto.
      const estadoEnCero = { ...currentState, posicionFisica: posicionAbsolutaCero };
      timelapseNextRef.current = { currentState: estadoEnCero, nuevasEjecutadas };

      // Si ya está en 0 no hace falta moverse: arrancamos la espera directamente y nos
      // ahorramos el viaje de ida y vuelta por Bluetooth.
      if (Math.abs(pulsosCero - pulsosActualesTL) < 5) {
        timelapseMovingToCeroRef.current = false;
        iniciarEsperaTimelapse(currentState.tiempoEsperaTimelapse);
        return;
      }

      // Movimiento real a 0: marcamos el ref síncrono para que el handler de COMPLETADO
      // sepa que debe iniciar la espera (y no caer en el flujo normal de hilado).
      timelapseMovingToCeroRef.current = true;
      sendCommand('MOTOR_PRINCIPAL_MOVETO', {
        pasos: pulsosCero,
        velocidad: currentState.motorConfig.velocidadPrincipalAuto || 400,
        aceleracion: currentState.motorConfig.aceleracionPrincipalAuto || 1800
      });

      return;
    }

    // Sin timelapse o sin hilador: paso normal sin foto
    stats.lastMarkOutcome = 'noPhoto';

    avanzarASiguientePosicionRef(currentState, nuevasEjecutadas);
  };

  // Predicción de tiempo restante (segundos). Modela timelapse: para cada paso
  // restante calcula la probabilidad de foto según la curva cuadrática y suma
  // P(foto)*tPhoto + (1-P)*tNoPhoto. Retorna null hasta que hay datos suficientes.
  const predecirTiempoRestante = (currentState, nuevasEjecutadas, total) => {
    const stats = statsRunRef.current;
    const hechos = nuevasEjecutadas.length;
    const faltantes = total - hechos;
    if (faltantes <= 0) return 0;

    const tNoP = stats.tNoPhotoCount > 0 ? stats.tNoPhotoSum / stats.tNoPhotoCount : null;
    const tP = stats.tPhotoCount > 0 ? stats.tPhotoSum / stats.tPhotoCount : null;
    if (tNoP === null && tP === null) return null;

    // Fallbacks cruzados
    const waitTL = (currentState.tiempoEsperaTimelapse || 3) * 1000 + 1500; // espera + viaje aprox
    const tNoPhotoEst = tNoP !== null ? tNoP : Math.max(500, (tP || 0) - waitTL);
    const tPhotoEst = tP !== null ? tP : ((tNoP || 1000) + waitTL);

    const enTimelapse = !!(
      currentState.modoTimelapse &&
      !currentState.modoClavado &&
      currentState.secuenciaSeleccionada?.herramienta === 'hilador'
    );

    if (!enTimelapse) {
      return Math.round((faltantes * tNoPhotoEst) / 1000);
    }

    const iMax = Math.max(1, currentState.fotoIntervaloMax || 10);
    let predicho = 0;
    for (let k = hechos; k < total; k++) {
      const progreso = k / total;
      const intervalo = 1 + (iMax - 1) * progreso * progreso;
      const pPhoto = Math.min(1, 1 / intervalo);
      predicho += pPhoto * tPhotoEst + (1 - pPhoto) * tNoPhotoEst;
    }
    return Math.round(predicho / 1000);
  };

  const avanzarASiguientePosicionRef = (currentState, nuevasEjecutadas) => {
    const { 
      secuenciaSeleccionada, 
      posicionActualIndex, 
      posicionFisica, 
      motorConfig, 
      manualOffset,
      modoClavado,
      posicionActualClavado,
      totalClavos: totalClavosState
    } = currentState;

    if (modoClavado) {
      let siguienteClavo = posicionActualClavado + 1;
      while (siguienteClavo < totalClavosState) {
        if (!nuevasEjecutadas.includes(siguienteClavo)) break;
        siguienteClavo++;
      }

      if (siguienteClavo < totalClavosState) {
        const posicionAbsolutaLogica = calcularPosicionAbsolutaPura(siguienteClavo, posicionFisica);
        const pulsosFinales = convertirAPulsos(posicionAbsolutaLogica) + manualOffset;
        
        setPosicionActualClavado(siguienteClavo);
        setPosicionFisica(posicionAbsolutaLogica);

        sendCommand('MOTOR_PRINCIPAL_MOVETO', {
          pasos: pulsosFinales,
          velocidad: motorConfig.velocidadPrincipalManual || 400,
          aceleracion: motorConfig.aceleracionPrincipalManual || 800
        });
      } else {
        setEjecutando(false);
        alert('🎉 Clavado perimetral completado!');
      }
      return;
    }

    if (!secuenciaSeleccionada) return;

    let siguienteIndex = posicionActualIndex + 1;
    
    while (siguienteIndex < secuenciaSeleccionada.posiciones.length) {
      if (!nuevasEjecutadas.includes(siguienteIndex)) {
        break;
      }
      siguienteIndex++;
    }

    if (siguienteIndex < secuenciaSeleccionada.posiciones.length) {
      // 1. Posición lógica pura (0-249)
      const posicionObjetivoLogica = secuenciaSeleccionada.posiciones[siguienteIndex];
      
      // 2. Cálculo del vector más corto en escala lógica
      const posicionAbsolutaLogica = calcularPosicionAbsolutaPura(posicionObjetivoLogica, posicionFisica); 
      
      // 3. Conversión a pulsos base y aplicación del desfase manual (en pulsos)
      let pulsosFinales = convertirAPulsos(posicionAbsolutaLogica) + manualOffset;
      let posicionAbsolutaLogicaNorm = posicionAbsolutaLogica;
      const pulsosActuales = convertirAPulsos(posicionFisica) + manualOffset;

      // Normalizar pulsos negativos: el ESP32 no maneja bien posiciones absolutas negativas.
      // Sumar una vuelta completa (4000 pasos) para llegar al mismo punto físico con valor positivo.
      if (pulsosFinales < 0) {
        pulsosFinales += 4000;
        posicionAbsolutaLogicaNorm += totalClavosRef.current;
      }

      setPosicionActualIndex(siguienteIndex);

      // Si el carro ya está en el destino NO se manda el movimiento. Un movimiento de cero
      // pasos termina en el acto, y en el ESP32 su aviso MOTOR_PRINCIPAL_COMPLETADO sale a la
      // vez que la confirmación de la orden por el mismo canal Bluetooth: uno pisa al otro,
      // el completado se pierde y la secuencia se queda esperando para siempre. Pasa en
      // clavos consecutivos, porque el propio hilado ya avanza el carro un clavo (ver
      // HILAR_COMPLETADO). Afecta a todo firmware con esa lógica, incluidas máquinas vendidas.
      //
      // Se conserva la posición REAL, no la ideal: con números de clavos que no dividen 4000
      // el avance del hilado se redondea, y así ese desfase se acumula a la vista y se corrige
      // con un movimiento de verdad en cuanto llega a 5 pulsos, en vez de crecer sin control.
      if (Math.abs(pulsosFinales - pulsosActuales) < 5) {
        const nextState = { ...currentState, posicionActualIndex: siguienteIndex };
        setTimeout(() => ejecutarEnPosicionActualRef(nextState), 50);
        return;
      }

      setPosicionFisica(posicionAbsolutaLogicaNorm);
      sendCommand('MOTOR_PRINCIPAL_MOVETO', {
        pasos: pulsosFinales,
        velocidad: motorConfig.velocidadPrincipalAuto || 50,
        aceleracion: motorConfig.aceleracionPrincipalAuto || 8000
      });
    } else {
      setEjecutando(false);
      alert('🎉 Secuencia completada exitosamente!');
    }
  };

  const ejecutarEnPosicionActualRef = (currentState) => {
    const { 
      secuenciaSeleccionada, 
      posicionesEjecutadas, 
      posicionActualIndex, 
      motorConfig,
      modoClavado,
      posicionesEjecutadasClavado,
      posicionActualClavado
    } = currentState;

    if (esperandoAccionRef.current) return;

    if (modoClavado) {
      if (posicionesEjecutadasClavado.includes(posicionActualClavado)) {
        avanzarASiguientePosicionRef(currentState, posicionesEjecutadasClavado);
        return;
      }
      fijarEsperandoAccion(true);
      sendCommand('DISPARAR_CLAVO', { tiempo: motorConfig.tiempoDisparo });
      return;
    }

    if (!secuenciaSeleccionada) return;

    if (posicionesEjecutadas.includes(posicionActualIndex)) {
      avanzarASiguientePosicionRef(currentState, posicionesEjecutadas);
      return;
    }

    fijarEsperandoAccion(true);

    if (secuenciaSeleccionada.herramienta === 'hilador') {
      sendCommand('HILAR', {
        anguloDentro: motorConfig.anguloDentro,
        anguloFuera: motorConfig.anguloFuera,
        pasosPrincipal: Math.round(4000 / totalClavosRef.current),
        velocidadPrincipal: motorConfig.velocidadPrincipalAuto,
        aceleracionPrincipal: motorConfig.aceleracionPrincipalAuto, 
        velocidadHilador: motorConfig.velocidadHilador,
        aceleracionHilador: motorConfig.aceleracionHilador,
        umbralPulsos: 0 // Detección deshabilitada en Arduino, la maneja React
      });
    } else {
      sendCommand('DISPARAR_CLAVO', { tiempo: motorConfig.tiempoDisparo });
    }
  };

  // Escuchar notificaciones del ESP32 (Ahora seguro contra loops)
  useEffect(() => {
    if (!isConnected || !onStatusChange) return;
    
    // Mostrar homing INMEDIATAMENTE al conectar, sin esperar respuesta del ESP32
    setShowHomingModal(true);

    // Al conectar, pedir el estado para sincronizar posición y enviar CONFIGURACIÓN
    sendCommand('ESTADO');
    
    // Si tenemos configuración cargada, la enviamos al ESP32 para asegurar sincronía de "invertir"
    if (configCargada && motorConfig) {
      sendCommand('CONFIG_MOTORES', {
        invertirPrincipal: motorConfig.invertirPrincipal || false,
        invertirHilador: motorConfig.invertirHilador || false,
        invertirElevador: motorConfig.invertirElevador || false,
        invertirAuxiliar: motorConfig.invertirAuxiliar || false,
        invertirRele: motorConfig.invertirRele || false
      });
    }

    const handleStatusUpdate = (data) => {
      const currentState = stateRef.current;
      console.log('Notificación recibida en Base:', data);
      
      if (data.cmd === 'ESTADO' && data.estado) {
        const pasosArduino = data.estado.motorPrincipalPos ?? 0;
        const posLogicaSincronizada = pasosArduino / (4000 / totalClavosRef.current);
        console.log(`🔄 Sincronizando posición: Arduino=${pasosArduino} pasos -> App=${posLogicaSincronizada} u.l.`);
        setPosicionFisica(posLogicaSincronizada);
        if (data.estado.encoderCount !== undefined) {
          const enc = data.estado.encoderCount;
          setEncoderLiveValue(enc);
          // Rastrear el pico del ciclo actual (el Arduino resetea el contador en F4)
          if (enc > encoderMaxCicloRef.current) {
            encoderMaxCicloRef.current = enc;
          }
        }
        // No actualizamos motorConfig desde ESTADO — evita disparar efectos que envían CONFIG_MOTORES al Arduino
      } else if (data.cmd === 'CALLBACK' && data.evento === 'CONFIG_MOTORES' && data.status === 'OK') {
        setConfigSavedNotif(true);
        setTimeout(() => setConfigSavedNotif(false), 3000);
      } else if (data.evento === 'HILO_ROTO') {
        // Arduino no debería disparar esto (umbralPulsos=0), pero si llega lo ignoramos
        console.log(`⚠️ HILO_ROTO del Arduino ignorado (detección movida a React)`);
      } else if (data.evento === 'HILAR_COMPLETADO') {
        const { pulsosEncoder = 0, pasosMotor = 0 } = data;
        const umbral = stateRef.current.motorConfig.umbralPulsosEncoder ?? 50;
        const maxSospechosos = stateRef.current.motorConfig.ciclosSospechososMax ?? 2;

        console.log(`📡 HILAR_COMPLETADO — encoder=${pulsosEncoder}, umbral=${umbral}`);

        if (pulsosEncoder >= umbral) {
          encoderSospechososRef.current = 0;
          setEncoderHistory(prev => [...prev.slice(-49), { ts: Date.now(), pulsos: pulsosEncoder, tipo: 'ok' }]);
          console.log(`✅ Hilado OK. Encoder=${pulsosEncoder}`);
        } else {
          encoderSospechososRef.current += 1;
          const ciclos = encoderSospechososRef.current;
          console.log(`⚠️ Encoder bajo: ${pulsosEncoder} < ${umbral}, ciclosSospechosos=${ciclos}/${maxSospechosos}`);

          if (ciclos >= maxSospechosos) {
            encoderSospechososRef.current = 0;
            setEncoderHistory(prev => [...prev.slice(-49), { ts: Date.now(), pulsos: pulsosEncoder, tipo: 'roto' }]);
            setEjecutando(false);
            setPausado(true);
            fijarEsperandoAccion(false);
            setErrorHiloRoto(true);
            console.log(`🚨 HILO ROTO — ${ciclos} ciclos consecutivos con encoder < umbral`);
          } else {
            setEncoderHistory(prev => [...prev.slice(-49), { ts: Date.now(), pulsos: pulsosEncoder, tipo: 'sospechoso' }]);
          }
        }


        // En modo hilado NO se consumen clavos del cargador; solo en clavado.

        // El hilado mueve el motor principal `pasosMotor` pulsos (un clavo) para abrazar el
        // hilo, así que el carro ya no está donde la app lo dejó. Se refleja en la posición
        // conocida; si no, el siguiente clavo consecutivo pediría un movimiento de cero pasos
        // cuyo aviso de completado se pierde (ver avanzarASiguientePosicionRef).
        const pulsosPorClavo = 4000 / (totalClavosRef.current || 1);
        const posicionTrasHilar = currentState.posicionFisica + pasosMotor / pulsosPorClavo;
        setPosicionFisica(posicionTrasHilar);
        const estadoTrasHilar = { ...currentState, posicionFisica: posicionTrasHilar };

        if (esperandoAccionRef.current) {
          fijarEsperandoAccion(false);
          marcarPosicionComoEjecutadaRef(estadoTrasHilar);
        }
      } else if (data.cmd === 'DISPARAR_CLAVO' && data.status === 'ok') {
        console.log('✅ Clavo disparado');
        
        // Decrementar clavos y verificar si se acabaron
        setClavosRestantes(prev => {
          const nuevos = Math.max(0, prev - 1);
          if (nuevos === 0) {
            setEjecutando(false);
            setPausado(true);
            setErrorClavosAgotados(true);
          }
          return nuevos;
        });

        if (esperandoAccionRef.current) {
          // Aplicar delay configurable antes de continuar
          setTimeout(() => {
            fijarEsperandoAccion(false);
            marcarPosicionComoEjecutadaRef(currentState);
          }, currentState.motorConfig.delayDisparo || 500);
        }
      } else if (data.evento === 'MOTOR_PRINCIPAL_COMPLETADO') {
        console.log('✅ Movimiento completado');

        // Ref síncrono: si llegamos a 0 en timelapse, iniciar la espera. Esto NO depende
        // de stateRef (que puede estar desactualizado), evitando la condición de carrera.
        if (timelapseMovingToCeroRef.current) {
          timelapseMovingToCeroRef.current = false;
          console.log(`📸 Timelapse: en posición 0, esperando ${currentState.tiempoEsperaTimelapse}s...`);
          if (!currentState.pausado) {
            iniciarEsperaTimelapse(currentState.tiempoEsperaTimelapse);
          }
          return;
        }

        if (currentState.ejecutando && !currentState.pausado) {
          ejecutarEnPosicionActualRef(currentState);
        }
      }
    };

    const unsubscribe = onStatusChange(handleStatusUpdate);
    return unsubscribe;
  }, [isConnected]); // Solo depende de la conexión

  // Wrappers para la UI (usando estado directo para render)
  const calcularPosicionAbsoluta = (posicionObjetivo) => calcularPosicionAbsolutaPura(posicionObjetivo, posicionFisica);

  const marcarPosicionComoEjecutada = () => {
    marcarPosicionComoEjecutadaRef(stateRef.current);
  };

  const marcarPosicionActualSinAvanzar = () => {
    const currentState = stateRef.current;
    const { 
      posicionesEjecutadas, 
      posicionActualIndex, 
      modoClavado,
      posicionesEjecutadasClavado,
      posicionActualClavado
    } = currentState;

    if (modoClavado) {
      if (!posicionesEjecutadasClavado.includes(posicionActualClavado)) {
        setPosicionesEjecutadasClavado(prev => [...prev, posicionActualClavado]);
      }
    } else {
      if (!posicionesEjecutadas.includes(posicionActualIndex)) {
        setPosicionesEjecutadas(prev => [...prev, posicionActualIndex]);
      }
    }
  };
  
  const avanzarASiguientePosicion = () => {
    avanzarASiguientePosicionRef(stateRef.current, stateRef.current.posicionesEjecutadas);
  };
  
  const ejecutarEnPosicionActual = () => {
    ejecutarEnPosicionActualRef(stateRef.current);
  };
  
  // Iniciar secuencia
  const handlePlay = () => {
    if (!isConnected) {
      alert('Debes estar conectado al ESP32 para ejecutar');
      return;
    }

    // Solo bloquear por falta de clavos en modo clavado
    if (modoClavado && clavosRestantes <= 0) {
      setErrorClavosAgotados(true);
      return;
    }

    if (modoClavado) {
      if (pausado) {
        setPausado(false);
        setEjecutando(true);
        // No contaminar el promedio con el tiempo de pausa
        statsRunRef.current.lastMarkTime = null;
        statsRunRef.current.lastMarkOutcome = null;
        ejecutarEnPosicionActual();
      } else {
        statsRunRef.current = {
          inicio: Date.now(),
          puntosAlEmpezar: posicionesEjecutadasClavado.length,
          lastMarkTime: null,
          lastMarkOutcome: null,
          tNoPhotoSum: 0, tNoPhotoCount: 0,
          tPhotoSum: 0, tPhotoCount: 0,
        };
        setEjecutando(true);
        setPausado(false);
        setTimeout(() => ejecutarEnPosicionActual(), 100);
      }
      return;
    }

    if (!secuenciaSeleccionada) {
      alert('Selecciona una secuencia');
      return;
    }
    
    if (pausado) {
      setPausado(false);
      setEjecutando(true);
      // No contaminar el promedio con el tiempo de pausa
      statsRunRef.current.lastMarkTime = null;
      statsRunRef.current.lastMarkOutcome = null;
      if (esperandoTimelapse && timelapseNextRef.current) {
        // Reanudar la espera de timelapse (el timer fue cancelado al pausar)
        iniciarEsperaTimelapse(tiempoEsperaTimelapse);
      } else {
        ejecutarEnPosicionActual();
      }
    } else {
      // Iniciar medición para tiempo estimado
      statsRunRef.current = {
        inicio: Date.now(),
        puntosAlEmpezar: posicionesEjecutadas.length,
        lastMarkTime: null,
        lastMarkOutcome: null,
        tNoPhotoSum: 0, tNoPhotoCount: 0,
        tPhotoSum: 0, tPhotoCount: 0,
      };
      fotoAccRef.current = 0; // reiniciar curva de fotos al empezar

      let primeraPosicion = 0;
      // Si estamos "reanudando" o empezando, deberíamos chequear si ya estamos en un índice válido
      // Pero como el usuario quiere empezar desde donde se quedó, usamos posicionActualIndex
      if (posicionActualIndex < secuenciaSeleccionada.posiciones.length) {
         primeraPosicion = posicionActualIndex;
      }
      
      // Buscar siguiente no ejecutada si la actual ya está hecha (opcional, pero buena UX)
      // El usuario pidió que se posicione en el último índice.
      
      if (primeraPosicion >= secuenciaSeleccionada.posiciones.length) {
        alert('Todas las posiciones ya están ejecutadas');
        return;
      }
      
      setPosicionActualIndex(primeraPosicion);
      setEjecutando(true);
      setPausado(false);
      
      setTimeout(() => {
        ejecutarEnPosicionActual();
      }, 100);
    }
  };
  
  // Pausar secuencia
  const handlePause = () => {
    setPausado(true);
    // Resetear mark time para que el siguiente intervalo no incluya la pausa
    statsRunRef.current.lastMarkTime = null;
    statsRunRef.current.lastMarkOutcome = null;
    if (timelapseTimerRef.current) {
      clearTimeout(timelapseTimerRef.current);
      timelapseTimerRef.current = null;
    }
  };
  
  // Detener secuencia (mantenido por compatibilidad pero no usado en UI)
  const handleStop = () => {
    setEjecutando(false);
    setPausado(false);
    setPosicionActualIndex(0);
    fijarEsperandoAccion(false);
    setEsperandoTimelapse(false);
    timelapseNextRef.current = null;
    timelapseMovingToCeroRef.current = false;
    if (timelapseTimerRef.current) {
      clearTimeout(timelapseTimerRef.current);
      timelapseTimerRef.current = null;
    }
  };
  
  // Reiniciar posiciones ejecutadas
  const handleReiniciar = () => {
    if (modoClavado) {
      if (window.confirm('¿Reiniciar todo el clavado perimetral? Se reiniciará también el contador de clavos.')) {
        setPosicionesEjecutadasClavado([]);
        setPosicionActualClavado(0);
        setClavosRestantes(0); // Forzar recarga física al reiniciar
      }
      return;
    }

    if (!secuenciaSeleccionada) return;
    
    if (window.confirm('¿Reiniciar todas las posiciones ejecutadas?')) {
      setPosicionesEjecutadas([]);
      setPosicionActualIndex(0); // También volver al principio
      
      // Actualización en Firebase ocurrirá por el useEffect
    }
  };

  const handleVolverMenuClavado = () => {
    if (ejecutando) {
      if (!window.confirm('La secuencia está en curso. ¿Deseas detenerla y volver al menú?')) {
        return;
      }
    }
    setEjecutando(false);
    setPausado(false);
    setModoClavadoIniciado(false);
  };

  const handleVolverMenuHilado = () => {
    if (ejecutando) {
      if (!window.confirm('La secuencia está en curso. ¿Deseas detenerla y volver a la galería?')) {
        return;
      }
    }
    setEjecutando(false);
    setPausado(false);
    setModoHiladoIniciado(false);
    setSecuenciaSeleccionada(null);
    setEsperandoTimelapse(false);
    timelapseNextRef.current = null;
    timelapseMovingToCeroRef.current = false;
    if (timelapseTimerRef.current) {
      clearTimeout(timelapseTimerRef.current);
      timelapseTimerRef.current = null;
    }
  };

  const handleVolverMenuTikTok = () => {
    setEjecutando(false);
    setPausado(false);
    fijarEsperandoAccion(false);
    setModoTikTokIniciado(false);
    setSecuenciaSeleccionada(null);
  };

  const handleTikTokEjecutarPosicion = () => {
    if (!isConnected || !secuenciaSeleccionada) return;
    const currentState = stateRef.current;
    avanzarASiguientePosicionRef(currentState, currentState.posicionesEjecutadas);
    setEjecutando(true);
    setPausado(false);
  };

  // Alternar estado de la posición actual
  const togglePosicionActualStatus = () => {
    if (modoClavado) {
      const nuevas = posicionesEjecutadasClavado.includes(posicionActualClavado)
        ? posicionesEjecutadasClavado.filter(p => p !== posicionActualClavado)
        : [...posicionesEjecutadasClavado, posicionActualClavado];
      setPosicionesEjecutadasClavado(nuevas);
      return;
    }

    if (!secuenciaSeleccionada) return;

    let nuevasEjecutadas;
    if (posicionesEjecutadas.includes(posicionActualIndex)) {
      nuevasEjecutadas = posicionesEjecutadas.filter(p => p !== posicionActualIndex);
    } else {
      nuevasEjecutadas = [...posicionesEjecutadas, posicionActualIndex];
    }

    setPosicionesEjecutadas(nuevasEjecutadas);
    // La actualización del objeto secuenciaSeleccionada se maneja en el listener de Firebase
  };

  // Función para cambiar manualmente el índice actual
  const setIndiceManual = (nuevoIndice) => {
    if (modoClavado) {
      if (nuevoIndice >= 0 && nuevoIndice < totalClavos) {
        setPosicionActualClavado(nuevoIndice);
        
        // Mover motor a la nueva posición seleccionada
        const posAbs = calcularPosicionAbsolutaPura(nuevoIndice, posicionFisica);
        const pulsos = convertirAPulsos(posAbs) + manualOffset;
        
        setPosicionFisica(posAbs);
        
        sendCommand('MOTOR_PRINCIPAL_MOVETO', {
          pasos: pulsos,
          velocidad: motorConfig.velocidadPrincipalManual || 400,
          aceleracion: motorConfig.aceleracionPrincipalManual || 800
        });
      }
      return;
    }

    if (!secuenciaSeleccionada) return;
    
    if (nuevoIndice >= 0 && nuevoIndice < secuenciaSeleccionada.posiciones.length) {
      setPosicionActualIndex(nuevoIndice);
      
      // Mover motor a la nueva posición seleccionada
      const posicionObjetivoLogica = secuenciaSeleccionada.posiciones[nuevoIndice];
      const posicionAbsolutaLogica = calcularPosicionAbsolutaPura(posicionObjetivoLogica, posicionFisica);
      const pulsosFinales = convertirAPulsos(posicionAbsolutaLogica) + manualOffset;

      setPosicionFisica(posicionAbsolutaLogica);

      sendCommand('MOTOR_PRINCIPAL_MOVETO', {
        pasos: pulsosFinales,
        velocidad: motorConfig.velocidadPrincipalManual || 400,
        aceleracion: motorConfig.aceleracionPrincipalManual || 800
      });
    }
  };
  
  const handleReparado = () => {
    setErrorHiloRoto(false);
    setConteoFallasHilo(0);
    fijarEsperandoAccion(false);
    // Reiniciamos estados para permitir usar "Iniciar" limpiamente
    setPausado(false);
    setEjecutando(false);
  };

  const handleEstablecerCero = () => {
    // 1. Decirle al ESP32 que la posición actual es el cero absoluto
    sendCommand('SET_CERO');
    // 2. Resetear posición física en la app
    setPosicionFisica(0);
    // 3. Resetear el desfase manual acumulado
    setManualOffset(0);
    // 4. Cerrar el modal
    setShowHomingModal(false);
  };

  const handleRecargarClavos = () => {
    setClavosRestantes(90);
    setErrorClavosAgotados(false);
    fijarEsperandoAccion(false);
    setPausado(false);
    setEjecutando(false);
  };

  const handleIniciarClavadoNuevo = () => {
    setConfirmModalConfig({
      titulo: '¿Comenzar clavado nuevo?',
      mensaje: 'Se perderá el progreso del anterior y se reiniciarán las posiciones y el contador de clavos.',
      onConfirm: () => {
        setPosicionesEjecutadasClavado([]);
        setPosicionActualClavado(0);
        setClavosRestantes(0); // Forzar recarga física al iniciar secuencia nueva
        setModoClavadoIniciado(true);
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  const handleContinuarClavado = () => {
    setModoClavadoIniciado(true);
  };

  if (loadingAuth) {
    return (
      <div className="fixed inset-0 bg-slate-900 z-[9999] flex items-center justify-center text-white">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  // MODO CONTROL REMOTO: hay otra sesión (tablet) activa con BLE; este dispositivo solo controla.
  // Va ANTES del bloqueo de orientación para permitir uso en vertical en el teléfono.
  if (isRemoteMode) {
    return (
      <RemoteControl
        db={db}
        user={user}
        estado={remoteEstado}
        sessionId={sessionIdRef.current}
        isAdmin={esAdminFlag}
        onSignOut={() => signOut(auth)}
        onUsarAppCompleta={usarAppCompleta}
      />
    );
  }

  // Esperar al primer snapshot de controlRemoto antes de aplicar el bloqueo de orientación.
  // Evita el flash "Gira tu dispositivo" en el teléfono justo después de loguearse.
  if (!remoteEstadoCargado) {
    return (
      <div className="fixed inset-0 bg-slate-900 z-[9999] flex items-center justify-center text-white">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isLandscape) {
    return (
      <div className="fixed inset-0 bg-slate-900 z-[9999] flex flex-col items-center justify-center text-white p-6 text-center">
        <div className="text-6xl mb-6 animate-bounce">📱↻</div>
        <h2 className="text-3xl font-bold mb-3">Gira tu dispositivo</h2>
        <p className="text-slate-400 text-lg max-w-md">
          Para una mejor experiencia y control de la máquina CNC, por favor utiliza la aplicación en modo horizontal.
        </p>
      </div>
    );
  }

  // Ya no hay pantalla de "Cuenta en revisión": cualquier cuenta entra directo.
  // El campo users.estado se conserva solo para el registro de ESP32/firmware.

  // Props del sidebar (reutilizadas en dos lugares)
  const sequenceListProps = {
    showFormulario,
    setShowFormulario,
    espConnectionProps: {
      isConnected,
      isConnecting,
      error: bleError,
      deviceName,
      isBluetoothAvailable,
      isSecureContext,
      connect: connectBluetooth,
      disconnect: disconnectBluetooth
    },
    motorConfig,
    setMotorConfig,
    modoClavado,
    setModoClavado,
    modoTikTok,
    setModoTikTok,
    modoPuntos,
    setModoPuntos,
    user,
    userData,
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden relative">

      {/* AVISO: este dispositivo forzó la app completa mientras otra sesión (tablet) controla el BLE */}
      {hayOtraSesionPrimary && preferirAppCompleta && avisoSesionOcultoPara !== remoteEstado.sessionId && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-200 text-xs px-3 sm:px-4 py-2 flex items-center justify-center gap-2 sm:gap-3 flex-wrap relative">
          <span className="flex items-center gap-2 pr-6 sm:pr-0">
            <Smartphone size={14} className="shrink-0" />
            <span>
              Otro dispositivo tiene el Bluetooth de la máquina
              {remoteEstado.secuenciaNombre ? <> y está en <b className="text-amber-100">«{remoteEstado.secuenciaNombre}»</b></> : null}.
              Aquí puedes gestionar secuencias sin conectar.
            </span>
          </span>
          <button
            onClick={usarControlRemoto}
            className="px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-100 font-bold transition-colors"
          >
            Ir al control remoto
          </button>
          <button
            onClick={() => setAvisoSesionOcultoPara(remoteEstado.sessionId)}
            className="absolute right-2 top-1/2 -translate-y-1/2 sm:static sm:translate-y-0 p-1 rounded-md text-amber-300/70 hover:text-amber-100 hover:bg-amber-500/20 transition-colors"
            title="Ocultar este aviso"
            aria-label="Ocultar aviso"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Acceso al control remoto desde la app completa.
          Útil cuando un teléfono no se detectó como tal (la detección teléfono/tablet
          no es fiable) y abrió la app completa. Se oculta si otra sesión ya tiene el BLE,
          porque en ese caso el aviso de arriba ya ofrece "Volver a control remoto". */}
      {!hayOtraSesionPrimary && (
        <button
          onClick={usarControlRemoto}
          className="fixed bottom-6 left-6 z-[9000] bg-slate-800/90 backdrop-blur-sm text-slate-200 px-4 py-2.5 rounded-full shadow-2xl hover:bg-slate-700 transition-all hover:scale-105 border border-slate-600 flex items-center gap-2 font-bold text-sm"
          title="Abrir el control remoto en este dispositivo (ideal para el celular)"
        >
          <Smartphone size={18} className="text-blue-400" />
          Control remoto
        </button>
      )}

      {/* BOTÓN FLOTANTE DE ADMIN (Solo si isAdmin === true en Firestore, o es el email de admin como fallback) */}
      {user && (userData?.isAdmin === true || esCorreoAdmin(user.email)) && (
         <button
           onClick={() => setShowAdminPanel(true)}
           className="fixed bottom-6 right-6 z-[9000] bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-500 transition-all hover:scale-110 border-4 border-slate-900 group"
           title="Panel de Administración"
         >
           <Shield size={28} className="group-hover:rotate-12 transition-transform" />
           <span className="absolute right-full mr-4 bg-slate-800 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-700 font-bold pointer-events-none">
             Panel Admin
           </span>
         </button>
      )}

      {/* MODAL DE ADMIN */}
      {showAdminPanel && <AdminPanel db={db} currentUserId={user?.uid} onClose={() => setShowAdminPanel(false)} />}

      {/* MODAL REINICIAR PROGRESO */}
      {resetModalSec && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => !resetLoading && setResetModalSec(null)}
        >
          <div
            className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-96 flex flex-col items-center gap-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <p className="text-white font-bold text-lg mb-1">Reiniciar progreso</p>
              <p className="text-slate-400 text-sm">
                <span className="text-white font-semibold">"{resetModalSec.nombre}"</span> volverá a empezar desde cero.
              </p>
            </div>

            {/* Visual: estado actual → estado tras reset */}
            <div className="flex items-center justify-center gap-4 w-full">
              {/* Imagen con progreso actual */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-28 h-28 bg-white rounded-full shadow-inner overflow-hidden border-4 border-slate-600 flex items-center justify-center">
                  <SequenceSnapshot secId={resetModalSec.id} positions={resetModalSec.posiciones} size={104} strokeWidth={grosorTrazo} />
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {resetModalSec.ultimaPosicion > 0
                    ? `${Math.round((resetModalSec.ultimaPosicion / resetModalSec.posiciones.length) * 100)}% completado`
                    : 'Sin avance'}
                </span>
              </div>

              {/* Flecha */}
              <div className="flex flex-col items-center gap-1 text-orange-400/70">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </div>

              {/* Círculo vacío: estado tras reset */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-28 h-28 bg-white rounded-full shadow-inner border-4 border-slate-600 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300/40" />
                </div>
                <span className="text-[10px] font-bold text-orange-400/80 uppercase tracking-widest">0% — vacío</span>
              </div>
            </div>
            {/* Alerta irreversible */}
            <div className="w-full flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-300 text-xs leading-relaxed font-medium">
                Esta acción <span className="font-black uppercase">no se puede revertir</span>. El progreso guardado se eliminará permanentemente y la secuencia deberá ejecutarse desde el principio.
              </p>
            </div>

            <div className="flex gap-3 w-full">
              <button
                onClick={() => setResetModalSec(null)}
                disabled={resetLoading}
                className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-slate-200 font-bold text-sm transition-all disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarReset}
                disabled={resetLoading}
                className="flex-1 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/35 active:bg-red-500/50 border border-red-500/40 text-red-300 font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resetLoading ? <Loader size={16} className="animate-spin" /> : null}
                {resetLoading ? 'Reiniciando…' : 'Sí, reiniciar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REASIGNAR SECUENCIA A OTRO USUARIO (solo admin) */}
      {reasignarSec && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !reasignarLoading && setReasignarSec(null)}
        >
          <div
            className="bg-slate-800 border border-slate-700 rounded-2xl p-8 w-96 max-w-full flex flex-col items-center gap-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <p className="text-white font-bold text-lg mb-1 flex items-center justify-center gap-2">
                <User size={18} className="text-blue-400" />
                Asignar a otro usuario
              </p>
              <p className="text-slate-400 text-sm">
                <span className="text-white font-semibold">"{reasignarSec.nombre}"</span> aparecerá en la cuenta del usuario que elijas.
              </p>
            </div>

            {/* Vista previa */}
            <div className="w-28 h-28 bg-white rounded-full shadow-inner overflow-hidden border-4 border-slate-600 flex items-center justify-center">
              <SequenceSnapshot secId={reasignarSec.id} positions={reasignarSec.posiciones} size={104} strokeWidth={grosorTrazo} />
            </div>

            {/* Selector de usuario destino */}
            <div className="w-full">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Usuario destino</label>
              <select
                value={reasignarTarget}
                onChange={(e) => setReasignarTarget(e.target.value)}
                disabled={reasignarLoading}
                className="w-full bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-blue-500 cursor-pointer disabled:opacity-50"
              >
                {usuariosLista.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.id === user?.uid ? `Yo — ${u.email}` : u.email}
                  </option>
                ))}
              </select>
              {reasignarTarget && reasignarTarget === reasignarSec.uid && (
                <p className="text-[11px] text-slate-500 mt-2">Ya pertenece a este usuario.</p>
              )}
            </div>

            <div className="flex gap-3 w-full">
              <button
                onClick={() => setReasignarSec(null)}
                disabled={reasignarLoading}
                className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-slate-200 font-bold text-sm transition-all disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarReasignar}
                disabled={reasignarLoading || !reasignarTarget || reasignarTarget === reasignarSec.uid}
                className="flex-1 py-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/35 active:bg-blue-500/50 border border-blue-500/40 text-blue-300 font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {reasignarLoading ? <Loader size={16} className="animate-spin" /> : null}
                {reasignarLoading ? 'Asignando…' : 'Asignar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BOTÓN LATERAL PARA ABRIR MENÚ (visible solo cuando el sidebar está cerrado) ── */}
      {!sidebarAbierto && (
        <button
          onClick={() => setSidebarAbierto(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-1.5 py-4 px-1.5 bg-slate-800 border border-slate-700/70 border-l-0 rounded-r-xl shadow-xl transition-all hover:bg-slate-700 hover:px-2.5 active:scale-95 group"
          title="Abrir menú"
        >
          <Menu size={18} className="text-slate-300" />
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)] animate-pulse' : 'bg-slate-600'}`} />
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-200 transition-colors" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {modoTikTok ? '📺 TikTok' : modoClavado ? '🔨 Clavado' : '🧵 Hilado'}
          </span>
        </button>
      )}

      {/* ── SIDEBAR + BACKDROP ──────────────────────────────────────────── */}

      {/* Backdrop */}
      {sidebarAbierto && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarAbierto(false)}
        />
      )}

      {/* SIDEBAR DRAWER */}
      <div className={`fixed top-0 left-0 h-full z-50 w-72 bg-slate-950 border-r border-slate-700/60 shadow-2xl transition-transform duration-300 ease-in-out ${sidebarAbierto ? 'translate-x-0' : '-translate-x-full'}`}>
        <SequenceList
          {...sequenceListProps}
          sidebar={true}
          onClose={() => setSidebarAbierto(false)}
        />
      </div>

      {/* ── CONTENIDO PRINCIPAL (pantalla completa) ─────────────────────── */}
      <div className="w-full h-pantalla overflow-auto p-2">
        <div className="h-full">
          
          
          {/* Área Central: Formulario o Controlador */}
          {showFormulario ? (
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 h-full">
              <SequenceForm
                db={db}
                onClose={() => setShowFormulario(false)}
                clavosIniciales={totalClavos}
                esAdmin={esAdminFlag}
              />
            </div>
          ) : modoPuntos && FEATURE_PUNTOS ? (
            /* SECCIÓN PUNTOS: imagen → nube de puntos para clavar */
            <PuntosPanel
              db={db}
              user={user}
              onVolver={() => setModoPuntos(false)}
            />
          ) : modoTikTok && puedeUsarTikTok && !modoTikTokIniciado ? (
            /* PANTALLA: GALERÍA DE TIKTOK LIVE — selección de secuencia */
            <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700/50 h-full flex flex-col p-8 animate-in fade-in duration-300 overflow-hidden relative">
              <div className="flex justify-between items-center mb-8 shrink-0 flex-wrap gap-3">
                <h2 className="text-2xl font-light text-white flex items-center gap-3">
                  <span className="text-2xl">📺</span>
                  TikTok Live — Selecciona una Secuencia
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <div className="flex gap-6 flex-wrap pb-4">
                  {secuenciasRecientes.filter(s => s.herramienta === 'hilador').map((sec) => {
                    const total = sec.posiciones?.length || 0;
                    const done = sec.posicionesEjecutadas?.length || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => {
                          setSecuenciaSeleccionada(sec);
                          setPosicionActualIndex(sec.ultimaPosicion || 0);
                          setPosicionesEjecutadas(sec.posicionesEjecutadas || []);
                          setModoTikTokIniciado(true);
                        }}
                        className="group relative bg-slate-700/30 hover:bg-slate-700/60 border border-slate-600/50 hover:border-pink-500/50 rounded-3xl p-6 transition-all w-48 h-80 flex flex-col items-center gap-3 shrink-0 shadow-xl hover:shadow-pink-500/10"
                      >
                        <div className="w-28 h-28 bg-white rounded-full shadow-inner overflow-hidden border-4 border-slate-600 group-hover:border-pink-500/50 transition-colors flex items-center justify-center">
                          <SequenceSnapshot secId={sec.id} positions={sec.posiciones} size={104} strokeWidth={grosorTrazo} />
                        </div>
                        <div className="text-center flex-1 flex flex-col justify-center">
                          <div className="text-white font-bold text-sm group-hover:text-pink-200 transition-colors leading-tight">{sec.nombre}</div>
                          <div className="text-slate-400 text-xs mt-1">{total} puntos</div>
                          {done > 0 && (
                            <div className="mt-2">
                              <div className="h-1 bg-slate-600 rounded-full overflow-hidden w-24 mx-auto">
                                <div className="h-full bg-pink-500 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <div className="text-[10px] text-pink-400 mt-1 font-bold">{pct}% completado</div>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {secuenciasRecientes.filter(s => s.herramienta === 'hilador').length === 0 && (
                    <div className="text-slate-500 text-sm p-8">No hay secuencias de hilado disponibles. Crea una primero en modo Hilado.</div>
                  )}
                </div>
              </div>
            </div>
          ) : modoTikTok && puedeUsarTikTok && modoTikTokIniciado ? (
            /* PANTALLA: TIKTOK LIVE PANEL — ejecución por regalos */
            <TikTokLivePanel
              secuenciaSeleccionada={secuenciaSeleccionada}
              posicionesEjecutadas={posicionesEjecutadas}
              posicionActualIndex={posicionActualIndex}
              ejecutando={ejecutando}
              pausado={pausado}
              esperandoAccion={esperandoAccion}
              isConnected={isConnected}
              onEjecutarPosicion={handleTikTokEjecutarPosicion}
              onVolverMenu={handleVolverMenuTikTok}
              totalPosiciones={secuenciaSeleccionada?.posiciones?.length || 0}
            />
          ) : !modoClavado && !modoTikTok && !modoHiladoIniciado ? (
            /* NUEVA PANTALLA: GALERÍA DE HILADO */
            <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700/50 h-full flex flex-col p-8 animate-in fade-in duration-300 overflow-hidden relative">
              <div className="flex justify-between items-center mb-8 shrink-0 flex-wrap gap-3">
                <h2 className="text-2xl font-light text-white flex items-center gap-3">
                  <Target className="text-blue-500" size={28} />
                  Selecciona una Secuencia de Hilado
                </h2>

                <div className="flex items-center gap-3 flex-wrap">
                  {/* Dropdown de usuario — solo admins */}
                  {(userData?.isAdmin === true || esCorreoAdmin(user?.email)) && usuariosLista.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-blue-400 shrink-0" />
                      <select
                        value={uidVisualizando || ''}
                        onChange={(e) => {
                          setUidVisualizando(e.target.value);
                          setLimiteSecuencias(4);
                        }}
                        className="bg-slate-800 border border-slate-600 text-slate-200 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 cursor-pointer max-w-[220px] truncate"
                      >
                        {usuariosLista.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.id === user?.uid ? `Yo — ${u.email}` : u.email}
                          </option>
                        ))}
                      </select>
                      {uidVisualizando !== user?.uid && (
                        <button
                          onClick={() => { setUidVisualizando(user.uid); setLimiteSecuencias(4); }}
                          title="Volver a mis secuencias"
                          className="text-xs text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-lg border border-slate-600 hover:border-slate-400"
                        >
                          Volver a las mías
                        </button>
                      )}
                    </div>
                  )}

                  <button 
                    onClick={() => setModoClavado(true)}
                    className="text-xs text-slate-500 hover:text-white flex items-center gap-2 transition-colors group"
                  >
                    Cambiar a Modo Clavado
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Slider de Grosor de Trazo 
              <div className="mb-8 bg-slate-700/30 p-4 rounded-xl border border-slate-600/50 max-w-xs mx-auto w-full">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Grosor del Hilo</label>
                  <span className="text-xs font-mono text-blue-400">{grosorTrazo.toFixed(2)}</span>
                </div>
                <input 
                  type="range" 
                  min="0.01" 
                  max="0.1" 
                  step="0.01" 
                  value={grosorTrazo} 
                  onChange={(e) => setGrosorTrazo(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
              </div>*/}

              {/* Banner de modo visualización admin */}
              {uidVisualizando && uidVisualizando !== user?.uid && (
                <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-300 text-xs font-bold shrink-0">
                  <Shield size={14} />
                  Viendo secuencias de: <span className="font-mono text-blue-400">{usuariosLista.find(u => u.id === uidVisualizando)?.email || uidVisualizando}</span>
                </div>
              )}

              <div className="flex flex-row items-center gap-6 overflow-x-auto custom-scrollbar pb-12 px-8 py-4 min-h-[400px]">
                {/* Botón Nueva Secuencia — oculto cuando admin ve secuencias de otro usuario */}
                {uidVisualizando === user?.uid && (
                  <button
                    onClick={() => setShowFormulario(true)}
                    className="group bg-blue-600/10 hover:bg-blue-600/20 border-2 border-dashed border-blue-500/30 hover:border-blue-500/50 rounded-3xl p-8 transition-all w-64 h-80 flex flex-col items-center justify-center gap-6 shrink-0 shadow-xl"
                  >
                    <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
                      <Plus size={48} />
                    </div>
                    <div className="text-blue-400 font-bold text-center uppercase tracking-widest leading-tight">
                      Crear Nueva<br/>Secuencia
                    </div>
                  </button>
                )}

                {secuenciasRecientes.map((sec) => {
                  const isPro = userData?.rol === 'pro';
                  const isLoadingLink = linkLoading[sec.id];
                  const yaCopiado = linkCopiado[sec.id];
                  const linkUrl = sec.linkGenerado ? `${RETRATO_BASE_URL}/?id=${sec.linkGenerado}` : null;

                  return (
                  <div
                    key={sec.id}
                    className="group bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/50 hover:border-blue-500/50 rounded-3xl transition-all w-72 shrink-0 relative overflow-hidden shadow-2xl flex flex-col"
                  >
                    {/* ── Área clickable principal (inicia hilado) ── */}
                    <div
                      onClick={() => {
                        setSecuenciaSeleccionada(sec);
                        setPosicionActualIndex(sec.ultimaPosicion || 0);
                        setPosicionesEjecutadas(sec.posicionesEjecutadas || []);
                        setModoHiladoIniciado(true);
                      }}
                      className="cursor-pointer flex flex-col items-center gap-2 p-6 flex-1 text-center"
                    >
                      {/* Indicador superior de acción */}
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all group-hover:scale-100 scale-50 z-20">
                        <Play size={24} className="text-blue-400 fill-current shadow-lg" />
                      </div>

                      {/* Menú de opciones (kebab): Reiniciar / Asignar (admin) */}
                      <div className="absolute top-4 left-4 z-30">
                        <button
                          onClick={(e) => { e.stopPropagation(); setMenuSecId(menuSecId === sec.id ? null : sec.id); }}
                          title="Opciones"
                          className="flex items-center justify-center w-8 h-8 rounded-xl border bg-slate-800/80 border-slate-600/60 text-slate-400 hover:text-white hover:border-slate-400 transition-all"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {menuSecId === sec.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-full left-0 mt-1 w-44 bg-slate-800 border border-slate-600/70 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
                          >
                            <button
                              onClick={(e) => { e.stopPropagation(); setMenuSecId(null); setResetModalSec(sec); }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs font-bold text-slate-300 hover:bg-slate-700/60 transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                                <path d="M3 3v5h5"/>
                              </svg>
                              Reiniciar progreso
                            </button>

                            {esAdminFlag && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setMenuSecId(null); abrirReasignar(sec); }}
                                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs font-bold text-blue-300 hover:bg-slate-700/60 transition-colors border-t border-slate-700/60"
                              >
                                <User size={14} />
                                Asignar a usuario
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Contenedor de Imagen + Fecha */}
                      <div className="relative shrink-0 mb-4 flex items-center justify-center">
                        <div className="w-52 h-52 bg-white rounded-full shadow-inner overflow-hidden group-hover:scale-105 transition-transform border-4 border-slate-800/50 flex items-center justify-center">
                          <SequenceSnapshot secId={sec.id} positions={sec.posiciones} size={190} strokeWidth={grosorTrazo} />
                        </div>
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[9px] text-blue-400 font-black uppercase tracking-[0.15em] bg-slate-900 border border-blue-500/40 px-3 py-1 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.5)] z-10 whitespace-nowrap">
                          {formatRelativeDate(sec.createdAt)}
                        </div>
                      </div>

                      <div className="flex-1 w-full flex flex-col justify-between py-2">
                        <div className="text-slate-200 font-black text-xl truncate px-2" title={sec.nombre}>
                          {sec.nombre}
                        </div>
                        <div className="mt-auto pt-2">
                          {sec.ultimaPosicion > 0 ? (
                            <div className="bg-green-500/10 text-green-500 text-[10px] font-bold py-1.5 px-4 rounded-full border border-green-500/20 flex items-center justify-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                              {Math.round((sec.ultimaPosicion / sec.posiciones.length) * 100)}% COMPLETADO
                            </div>
                          ) : (
                            <div className="bg-slate-800/50 text-slate-500 text-[10px] font-bold py-1.5 px-4 rounded-full border border-slate-700/50 uppercase tracking-widest">
                              Listo para empezar
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Sección PRO: Link de retrato ── */}
                    {isPro && (
                      <div className="border-t border-slate-700/60 px-4 py-3 bg-slate-900/40 flex flex-col gap-2">
                        {!sec.linkGenerado ? (
                          /* Sin link generado: botón para crear */
                          <button
                            onClick={() => generarLink(sec)}
                            disabled={isLoadingLink}
                            className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-indigo-600/15 hover:bg-indigo-600/30 border border-indigo-500/25 hover:border-indigo-500/50 text-indigo-300 hover:text-indigo-100 text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isLoadingLink ? (
                              <Loader size={13} className="animate-spin" />
                            ) : (
                              <Link size={13} />
                            )}
                            {isLoadingLink ? 'Generando link…' : 'Generar link PRO'}
                          </button>
                        ) : (
                          /* Link ya generado: abrir + copiar */
                          <div className="space-y-1.5">
                            <div className="text-[9px] text-indigo-400/70 font-bold uppercase tracking-widest flex items-center gap-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-400"></div>
                              Link generado
                            </div>
                            <div className="flex gap-2">
                              <a
                                href={linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/40 border border-indigo-500/30 text-indigo-200 text-xs font-bold transition-all"
                              >
                                <ExternalLink size={12} />
                                Abrir
                              </a>
                              <button
                                onClick={e => { e.stopPropagation(); copiarLink(sec.id, sec.linkGenerado); }}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-xs font-bold transition-all ${
                                  yaCopiado
                                    ? 'bg-green-600/20 border-green-500/30 text-green-300'
                                    : 'bg-slate-700/50 hover:bg-slate-700 border-slate-600/50 text-slate-300 hover:text-white'
                                }`}
                              >
                                {yaCopiado ? <Check size={12} /> : <Copy size={12} />}
                                {yaCopiado ? 'Copiado' : 'Copiar'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}

                {/* Botón Cargar más */}
                {hayMasSecuencias && (
                  <button
                    onClick={() => setLimiteSecuencias(prev => prev + 4)}
                    className="group bg-slate-700/20 hover:bg-slate-700/40 border-2 border-dashed border-slate-500/30 hover:border-slate-400/50 rounded-3xl p-8 transition-all w-48 h-80 flex flex-col items-center justify-center gap-4 shrink-0 shadow-xl"
                  >
                    <div className="w-16 h-16 rounded-full bg-slate-500/20 flex items-center justify-center text-slate-400 group-hover:text-white group-hover:scale-110 transition-all">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12l7 7 7-7"/>
                      </svg>
                    </div>
                    <div className="text-slate-400 group-hover:text-white font-bold text-center uppercase tracking-widest text-sm leading-tight transition-colors">
                      Cargar<br/>más
                    </div>
                  </button>
                )}
              </div>
            </div>
          ) : modoClavado && !modoClavadoIniciado ? (
            /* Pantalla de Selección de Inicio para Modo Clavado */
            <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700/50 h-full flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-200 relative">
              {/* Botón para volver al modo hilado si se equivocó de modo principal */}
              {/*<button 
                onClick={() => setModoClavado(false)}
                className="absolute top-4 left-4 text-slate-500 hover:text-white flex items-center gap-2 text-xs transition-colors group"
              >
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                Volver a Hilado
              </button>*/}

              <h2 className="text-3xl font-light text-white mb-4 flex items-center gap-3">
                <Target className="text-orange-500" size={32} />
                Modo Clavado Perimetral
              </h2>

              {/* Selector de número de clavos */}
              <div className="flex items-center justify-center gap-3 mb-8 bg-slate-700/30 px-5 py-3 rounded-xl border border-slate-600/50 w-fit mx-auto">
                <span className="text-slate-400 text-sm font-medium">Clavos en la mesa:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={clavosInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || /^\d+$/.test(raw)) setClavosInput(raw);
                  }}
                  onBlur={commitClavos}
                  className="bg-slate-800 border border-slate-600 text-white text-center font-bold text-lg rounded-lg w-20 px-2 py-1 focus:outline-none focus:border-orange-500"
                />
                <span className="text-slate-500 text-xs">({Math.round(4000 / totalClavos)} pasos/clavo)</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-2xl">
                {/* Opción Continuar */}
                <button 
                  onClick={handleContinuarClavado}
                  disabled={posicionesEjecutadasClavado.length === 0 && posicionActualClavado === 0}
                  className="group relative bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-slate-500 rounded-2xl p-6 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute top-4 right-4 text-slate-500 group-hover:text-blue-400 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                      <Play size={16} fill="currentColor" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-200 mb-2">Continuar Anterior</h3>
                  <p className="text-slate-400 text-sm mb-4">Retomar donde te quedaste.</p>
                  
                  {posicionesEjecutadasClavado.length > 0 && (
                    <div className="inline-block bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded border border-green-500/30">
                      Progreso: {Math.round((posicionesEjecutadasClavado.length / totalClavos) * 100)}% ({posicionesEjecutadasClavado.length}/{totalClavos})
                    </div>
                  )}
                </button>

                {/* Opción Nuevo */}
                <button 
                  onClick={handleIniciarClavadoNuevo}
                  className="group relative bg-slate-700/50 hover:bg-orange-900/20 border border-slate-600 hover:border-orange-500/50 rounded-2xl p-6 text-left transition-all"
                >
                  <div className="absolute top-4 right-4 text-slate-500 group-hover:text-orange-400 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                      <Plus size={16} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-200 group-hover:text-orange-100 mb-2">Nuevo Clavado</h3>
                  <p className="text-slate-400 group-hover:text-orange-200/70 text-sm">Empezar desde cero (0/{totalClavos}).</p>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 h-full">
              {/* Botón Volver al Menú Principal (Fuera del controlador) */}
              {!ejecutando && (
                <div className="flex justify-start mb-1 items-center gap-3">
                  <button
                    onClick={() => {
                      if (modoClavado) {
                        handleVolverMenuClavado();
                      } else {
                        handleVolverMenuHilado();
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800/40 hover:bg-slate-700/60 text-slate-400 hover:text-white rounded-lg border border-slate-700/50 transition-all text-[10px] font-bold uppercase tracking-widest shadow-sm group"
                  >
                    <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Volver al menú
                  </button>
                  {!modoClavado && (
                    <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 border border-blue-500/30 px-3 py-1.5 rounded-lg">
                      Retrato de {totalClavos} clavos — {Math.round(4000 / totalClavos)} pasos/clavo
                    </span>
                  )}
                </div>
              )}

              {/* Componente 3: Controlador Automático (Arriba - Ancho completo) */}
              <div className="shrink-0">
                <AutoTestController
                  secuenciaSeleccionada={modoClavado ? { nombre: 'Clavado Perimetral', posiciones: Array.from({length: totalClavos}, (_, i) => i) } : secuenciaSeleccionada}
                  posicionesEjecutadas={modoClavado ? posicionesEjecutadasClavado : posicionesEjecutadas}
                  posicionActualIndex={modoClavado ? posicionActualClavado : posicionActualIndex}
                  ejecutando={ejecutando}
                  pausado={pausado}
                  esperandoAccion={esperandoAccion}
                  isConnected={isConnected}
                  handlePlay={handlePlay}
                  handlePause={handlePause}
                  handleStop={() => {
                    if (modoClavado) {
                      handleVolverMenuClavado();
                    } else {
                      handleVolverMenuHilado();
                    }
                  }}
                  handleReiniciar={() => {
                    if (modoClavado) {
                      setModoClavadoIniciado(false); 
                    } else {
                      handleReiniciar();
                    }
                  }}
                  onToggleStatus={togglePosicionActualStatus}
                  setIndice={setIndiceManual}
                  errorHiloRoto={errorHiloRoto}
                  onReparado={handleReparado}
                  tiempoRestante={tiempoRestante}
                  clavosRestantes={clavosRestantes}
                  errorClavosAgotados={errorClavosAgotados}
                  onRecargarClavos={handleRecargarClavos}
                  isAdmin={userData?.isAdmin === true || esCorreoAdmin(user?.email)}
                  modoTimelapse={modoTimelapse}
                  setModoTimelapse={setModoTimelapse}
                  tiempoEsperaTimelapse={tiempoEsperaTimelapse}
                  setTiempoEsperaTimelapse={setTiempoEsperaTimelapse}
                  fotoIntervaloMax={fotoIntervaloMax}
                  setFotoIntervaloMax={setFotoIntervaloMax}
                  esperandoTimelapse={esperandoTimelapse}
                  esHilador={secuenciaSeleccionada?.herramienta === 'hilador'}
                />
              </div>

              {/* Fila inferior: Visualización y Control Manual (Lado a lado) */}
              <div className="grid grid-cols-6 gap-4  min-h-0 overflow-hidden">

                {/* Componente 5: Visualización de Salida */}
                <div className="h-full flex flex-col min-h-0 col-span-2">
                  {modoClavado ? (
                    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 h-full overflow-hidden">
                      <CircularPositionSelector
                        totalPoints={totalClavos}
                        posicionActual={posicionActualClavado}
                        ejecutadas={posicionesEjecutadasClavado}
                        bloqueado={isConnecting || (ejecutando && !pausado)} 
                        onMoveRequest={(nuevoIndice) => {
                          // Actualizamos estado local de posición
                          setPosicionActualClavado(nuevoIndice);
                          
                          // Calculamos posición física y enviamos comando
                          const posAbs = calcularPosicionAbsolutaPura(nuevoIndice, posicionFisica);
                          const pulsos = convertirAPulsos(posAbs) + manualOffset;
                          
                          setPosicionFisica(posAbs);
                          
                          sendCommand('MOTOR_PRINCIPAL_MOVETO', {
                            pasos: pulsos,
                            velocidad: motorConfig.velocidadPrincipalManual || 400,
                            aceleracion: motorConfig.aceleracionPrincipalManual || 800
                          });
                        }}
                      />
                    </div>
                  ) : secuenciaSeleccionada ? (
                    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 h-full flex flex-col overflow-hidden">
                       <OutputVisualization
                          sequence={secuenciaSeleccionada.posiciones}
                          positionToShow={posicionActualIndex}
                          totalPoints={totalClavos}
                       />
                    </div>
                  ) : (
                    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 h-full flex items-center justify-center text-slate-500">
                      <p>Selecciona una secuencia para ver la visualización</p>
                    </div>
                  )}
                </div>

                {/* Componente 4: Control Manual */}
                <div className="h-full flex flex-col min-h-0 col-span-4">
                  <ManualToolControl
                    isConnected={isConnected}
                    sendCommand={sendCommand}
                    motorConfig={motorConfig}
                    setMotorConfig={setMotorConfig}
                    setManualOffset={setManualOffset}
                    ejecutando={ejecutando}
                    pausado={pausado}
                    clavosRestantes={clavosRestantes}
                    setClavosRestantes={setClavosRestantes}
                    modoClavado={modoClavado}
                    onDisparoManual={marcarPosicionActualSinAvanzar}
                    encoderLiveValue={encoderLiveValue}
                    encoderHistory={encoderHistory}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>{/* fin contenido principal */}

      {/* Indicador EN VIVO + botón para cortar transmisión */}
      {videoTransmitiendo && (
        <div className="fixed top-4 right-4 z-[9999] flex items-center gap-2 bg-red-600/90 backdrop-blur-sm border border-red-400/50 rounded-full pl-3 pr-1 py-1 shadow-xl">
          <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
          <span className="text-white text-xs font-bold tracking-wider">EN VIVO</span>
          <button
            onClick={stopVideoSession}
            className="ml-1 px-2 py-0.5 rounded-full bg-red-800 hover:bg-red-900 text-white text-[10px] font-bold transition-colors"
            title="Cortar transmisión de video"
          >
            CORTAR
          </button>
        </div>
      )}
      {videoError && (
        <div className="fixed top-16 right-4 z-[9999] max-w-xs bg-red-900/90 border border-red-500/50 text-red-100 text-xs rounded-xl px-3 py-2 shadow-xl">
          ⚠️ Video: {videoError}
        </div>
      )}

      {/* Modal de Confirmación Genérico */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4 text-orange-400">
                <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                  <AlertTriangle size={28} />
                </div>
                <h3 className="text-xl font-bold text-white">{confirmModalConfig.titulo}</h3>
              </div>
              <p className="text-slate-300 leading-relaxed">
                {confirmModalConfig.mensaje}
              </p>
            </div>
            <div className="flex border-t border-slate-700">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-6 py-4 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors font-medium border-r border-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={confirmModalConfig.onConfirm}
                className="flex-1 px-6 py-4 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 transition-colors font-bold"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toast de configuración guardada */}
      {configSavedNotif && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10002] bg-emerald-700 border border-emerald-500 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-4 duration-200">
          <span className="text-emerald-300">✓</span>
          Configuración guardada en el Arduino
        </div>
      )}

      {/* Modal de Calibración / Homing */}
      {showHomingModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-800 border border-yellow-500/50 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">

            {/* Header */}
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center shrink-0 text-2xl">
                  🔧
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Calibración requerida</h3>
                  <p className="text-yellow-400 text-sm mt-1">
                    El ESP32 acaba de encender sin referencia de posición
                  </p>
                </div>
              </div>
            </div>

            {/* Instrucciones */}
            <div className="px-6 pt-5 pb-3">
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Mueve el motor con los botones hasta que la aguja quede en la{' '}
                <strong className="text-white">posición 0 física</strong> de la máquina.
                Luego presiona <strong className="text-yellow-400">Aquí es el cero</strong>.
              </p>

              {/* Controles manuales del motor principal */}
              {/* Fila 1: Movimiento grueso ±1 clavo */}
              <div className="mb-3">
                <p className="text-slate-500 text-xs text-center mb-2">Movimiento grueso — {Math.round(4000 / totalClavos)} pasos (1 clavo)</p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => sendCommand('MOTOR_PRINCIPAL_MOVER', { pasos: -Math.round(4000 / totalClavos), velocidad: motorConfig.velocidadPrincipalAuto || 400, aceleracion: motorConfig.aceleracionPrincipalAuto || 1800 })}
                    className="w-20 h-16 rounded-xl bg-blue-700 hover:bg-blue-600 active:bg-blue-800 text-white text-2xl font-bold flex items-center justify-center border border-blue-500 transition-colors select-none touch-none shadow-lg"
                  >
                    ◀◀
                  </button>
                  <div className="text-slate-400 text-xs text-center w-14">
                    <div className="text-2xl mb-1">🎯</div>
                    <span>±{Math.round(4000 / totalClavos)}</span>
                  </div>
                  <button
                    onClick={() => sendCommand('MOTOR_PRINCIPAL_MOVER', { pasos: Math.round(4000 / totalClavos), velocidad: motorConfig.velocidadPrincipalAuto || 400, aceleracion: motorConfig.aceleracionPrincipalAuto || 1800 })}
                    className="w-20 h-16 rounded-xl bg-blue-700 hover:bg-blue-600 active:bg-blue-800 text-white text-2xl font-bold flex items-center justify-center border border-blue-500 transition-colors select-none touch-none shadow-lg"
                  >
                    ▶▶
                  </button>
                </div>
              </div>

              {/* Fila 2: Ajuste fino ±1 paso */}
              <div>
                <p className="text-slate-500 text-xs text-center mb-2">Ajuste fino — 1 paso</p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => sendCommand('MOTOR_PRINCIPAL_MOVER', { pasos: -1, velocidad: motorConfig.velocidadPrincipalNormal || 400, aceleracion: motorConfig.aceleracionPrincipalNormal || 800 })}
                    className="w-20 h-16 rounded-xl bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-white text-2xl flex items-center justify-center border border-slate-600 transition-colors select-none touch-none shadow"
                  >
                    ◀
                  </button>
                  <div className="text-slate-400 text-xs text-center w-14">
                    <div className="text-2xl mb-1">🔬</div>
                    <span>±1</span>
                  </div>
                  <button
                    onClick={() => sendCommand('MOTOR_PRINCIPAL_MOVER', { pasos: 1, velocidad: motorConfig.velocidadPrincipalNormal || 400, aceleracion: motorConfig.aceleracionPrincipalNormal || 800 })}
                    className="w-20 h-16 rounded-xl bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-white text-2xl flex items-center justify-center border border-slate-600 transition-colors select-none touch-none shadow"
                  >
                    ▶
                  </button>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex border-t border-slate-700">
              <button
                onClick={() => setShowHomingModal(false)}
                className="flex-1 px-6 py-4 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors font-medium border-r border-slate-700"
              >
                Ignorar
              </button>
              <button
                onClick={handleEstablecerCero}
                className="flex-1 px-6 py-4 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 transition-colors font-bold"
              >
                ✅ Aquí es el cero
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Base;
